import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type {
  ActiveCall,
  Activity,
  Automation,
  AutomationRun,
  Budget,
  Channel,
  ChannelSummary,
  ChatStory,
  CurrencyCode,
  CustomEmoji,
  Doc,
  Expense,
  ExpenseCategory,
  ExpenseStatus,
  FileAccessGrant,
  FileAsset,
  FileCategory,
  FileComment,
  FileVisibility,
  GeneratedFileLength,
  LibraryFileKind,
  NewLibraryFile,
  SlideDeck,
  FinanceFile,
  FinanceReadTarget,
  FinanceReading,
  FinanceScope,
  FinanceSummary,
  FinancialStatement,
  Game,
  GameKind,
  GameMove,
  GameStanding,
  GameSummary,
  SoloGameImpact,
  SoloGameKind,
  SoloGameResult,
  SoloGameStanding,
  GifResult,
  IncomingWebhook,
  InternalUnfurl,
  LinkPreview,
  Meeting,
  MeetingGuestInvite,
  Membership,
  Message,
  MessageAttachment,
  ChannelReceipts,
  MessageReminder,
  Notification,
  NotificationLevel,
  NotificationSettings,
  QuietHoursMode,
  Organization,
  OutgoingWebhook,
  Milestone,
  PollInput,
  Project,
  PublicUser,
  ReactionResult,
  Reminder,
  ReminderCategory,
  Role,
  SavedMessage,
  ScheduledMessage,
  StatementKind,
  Sheet,
  RoomToolset,
  SheetSummary,
  Space,
  SpaceMember,
  SpaceOccupant,
  SpaceBook,
  SavedSpaceTemplate,
  SpaceTemplateName,
  StickerPack,
  Task,
  TaskComment,
  ThreadSummary,
  TimeEntry,
  VaultCategory,
  VaultEntry as VaultEntryView,
  VaultEntryInput,
  VaultEntrySecrets,
  VaultSession,
  VaultSortResult,
  VaultStatus,
  Whiteboard,
  WhiteboardScope,
  WhiteboardSummary,
  ScreenSnapshot,
  Widget,
  WidgetKind,
  WidgetSurface,
  WorkspaceInvite,
  WorkspaceFeature,
  WorkspaceStats,
  ActivityTargetType,
  Announcement,
  Department,
  OrgChart,
  Policy,
  PolicyAck,
  PolicyKind,
  CheckInConfig,
  CheckInEntry,
  LeaveKind,
  LeaveRequest,
  Celebration,
  CoffeePairing,
  CoffeeToday,
  LeaveStatus,
  OfficeAssignment,
  OfficeSite,
  OfficeTodayEntry,
  OnboardingProgress,
  OnboardingTemplate,
  PulseSurvey,
  SessionInfo,
  TeamNowEntry,
  TeamReport,
  TeamWorkloadEntry,
  WorkSchedule,
} from '@itsappy/shared';
import { messagePreview, SCREEN_SNAPSHOT_REFRESH_MS } from '@itsappy/shared';
// Kept out of the block above so the booking hooks at the foot of this file
// can be read — and moved — without hunting through 120 lines of imports.
import type { Booking, ResourceAvailability } from '@itsappy/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { toast } from '@/store/ui';

interface List<T> {
  items: T[];
  nextCursor?: string;
}

type ProjectWithStats = Project & { stats: Record<string, number> & { total: number } };
type SpaceWithOccupancy = Space & { occupancy: number; occupants: SpaceOccupant[] };

/* -------------------------------- org ---------------------------------- */

/** The active workspace with its administration settings. Owners only. */
export const useWorkspace = (enabled = true) =>
  useQuery({
    queryKey: ['workspace'],
    queryFn: () => api.get<Organization & { role: Role }>('/orgs/current'),
    staleTime: 60_000,
    enabled,
  });

export const useWorkspaceStats = (enabled = true) =>
  useQuery({
    queryKey: ['workspace-stats'],
    queryFn: () => api.get<WorkspaceStats>('/orgs/stats'),
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled,
  });

export const useWorkspaceInvites = (enabled = true) =>
  useQuery({
    queryKey: ['workspace-invites'],
    queryFn: () => api.get<List<WorkspaceInvite>>('/orgs/invites'),
    staleTime: 30_000,
    enabled,
  });

/** The code someone types to join this workspace. Same audience as invites. */
export const useJoinCode = (enabled = true) =>
  useQuery({
    queryKey: ['workspace-join-code'],
    queryFn: () => api.get<{ joinCode: string }>('/orgs/join-code'),
    staleTime: 30_000,
    enabled,
  });

export const useMembers = () =>
  useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<List<Membership>>('/orgs/members'),
    staleTime: 60_000,
    refetchInterval: 30_000,
  });

/**
 * One product given to, or taken from, a selection of people.
 *
 * Separated on purpose. Access used to be handed out only as a whole set, so
 * "give these five people chat" was expressed as a preset and quietly revoked
 * everything else each of them held. `add` and `remove` change one product and
 * leave the rest alone, which is what an administrator means every time they
 * point at a column rather than at a row.
 */
export function useSetMemberAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userIds,
      feature,
      enabled,
    }: {
      userIds: string[];
      feature: WorkspaceFeature;
      enabled: boolean;
    }) =>
      api.post<{ updated: number; skipped: number }>('/orgs/members/bulk-features', {
        userIds,
        features: [feature],
        mode: enabled ? 'add' : 'remove',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['members'] });
      void qc.invalidateQueries({ queryKey: ['workspace-stats'] });
    },
  });
}

/**
 * A product switched on or off for the whole workspace.
 *
 * The session's own access is re-read afterwards for the same reason
 * Administration does it: this switch applies to the person who flipped it
 * too, and navigation that still shows a product nobody can open is the most
 * confusing possible outcome of a successful save.
 */
export function useSetWorkspaceFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      feature,
      enabled,
      disabledFeatures,
    }: {
      feature: WorkspaceFeature;
      enabled: boolean;
      disabledFeatures: WorkspaceFeature[];
    }) =>
      api.patch<Organization>('/orgs/current', {
        settings: {
          disabledFeatures: enabled
            ? disabledFeatures.filter((item) => item !== feature)
            : [...new Set([...disabledFeatures, feature])],
        },
      }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['workspace'] }),
        qc.invalidateQueries({ queryKey: ['workspace-stats'] }),
        qc.invalidateQueries({ queryKey: ['members'] }),
        useAuth.getState().bootstrap(),
      ]);
    },
  });
}

export interface AttendanceSummary {
  userId: string;
  firstSeenAt: string;
  lastActivityAt: string;
  activeSeconds: number;
  online: boolean;
  currentActivity?: {
    label: string;
    path: string;
    updatedAt: string;
    screenSharing: boolean;
  };
}

export const useAttendance = () =>
  useQuery({
    queryKey: ['attendance', new Date().toISOString().slice(0, 10)],
    queryFn: () => api.get<{ day: string; items: AttendanceSummary[] }>('/attendance'),
    refetchInterval: 15_000,
  });

/** Flat member list — handy for assignee pickers and mention lookups. */
export function useMemberUsers(): PublicUser[] {
  const { data } = useMembers();
  return data?.items.map((m) => m.user) ?? [];
}

/* ------------------------------- spaces -------------------------------- */

export const useSpaces = (enabled = true) =>
  useQuery({
    queryKey: ['spaces'],
    queryFn: () => api.get<List<SpaceWithOccupancy>>('/spaces'),
    refetchInterval: 20_000,
    enabled,
  });

/**
 * Puts the spaces in the order this person dragged them into.
 *
 * The move shows immediately: a card dropped in its new place has to stay
 * there, not spring back to the old cache until a refetch lands.
 */
export function useReorderSpaces() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<{ ids: string[] }>('/spaces/reorder', { ids }),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: ['spaces'] });
      const snapshot = qc.getQueryData<List<SpaceWithOccupancy>>(['spaces']);
      qc.setQueryData<List<SpaceWithOccupancy>>(['spaces'], (old) => {
        if (!old) return old;
        const byId = new Map(old.items.map((space) => [space.id, space]));
        const listed = new Set(ids);
        return {
          ...old,
          items: [
            ...ids.flatMap((id) => byId.get(id) ?? []),
            ...old.items.filter((space) => !listed.has(space.id)),
          ],
        };
      });
      return { snapshot };
    },
    onError: (_error, _ids, context) => {
      if (context?.snapshot) qc.setQueryData(['spaces'], context.snapshot);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['spaces'] }),
  });
}

/* --------------------------- space library ---------------------------- */

export const useSpaceBooks = (spaceId?: string) =>
  useQuery({
    queryKey: ['books', spaceId],
    queryFn: () => api.get<List<SpaceBook>>(`/books?spaceId=${spaceId}`),
    enabled: Boolean(spaceId),
  });

export function useCreateSpaceBook(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      author: string;
      description?: string;
      coverUrl?: string;
      ownerId?: string;
    }) => api.post<SpaceBook>('/books', { ...body, spaceId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['books', spaceId] }),
  });
}

export function useToggleBookRead(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookId: string) => api.post<SpaceBook>(`/books/${bookId}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['books', spaceId] }),
  });
}

export function useDeleteSpaceBook(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookId: string) => api.delete(`/books/${bookId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['books', spaceId] }),
  });
}

export const useSpace = (id?: string) =>
  useQuery({
    queryKey: ['space', id],
    queryFn: () => api.get<Space>(`/spaces/${id}`),
    enabled: Boolean(id),
  });

export const useSpaceChannel = (id?: string) =>
  useQuery({
    queryKey: ['space-channel', id],
    queryFn: () => api.get<Channel>(`/spaces/${id}/channel`),
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
  });

/**
 * Everything the room you are standing in comes with — board, conversation,
 * notes — opened the first time anybody asks for it.
 *
 * Long `staleTime`: the answer is three ids that do not change once the room
 * has them, and the request is what creates them, so re-asking on every
 * remount would be a write per re-render.
 */
export const useRoomTools = (spaceId?: string, zoneId?: string, enabled = true) =>
  useQuery({
    queryKey: ['room-tools', spaceId, zoneId],
    queryFn: () => api.get<RoomToolset>(`/spaces/${spaceId}/zones/${zoneId}/tools`),
    enabled: Boolean(spaceId && zoneId && enabled),
    staleTime: 10 * 60_000,
    // A guest has no business opening a room's board, and the panel says so
    // rather than retrying a 403 three times on the way to saying it.
    retry: false,
  });

export function useUpdateSpace(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Space>) => api.patch<Space>(`/spaces/${id}`, body),
    onSuccess: (space) => {
      qc.setQueryData(['space', id], space);
      qc.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

export function useCreateSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      description?: string;
      template?: string;
      /** A layout this org saved; the API refuses both at once. */
      templateId?: string;
    }) => api.post<Space>('/spaces', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spaces'] }),
  });
}

/* --------------------------- saved layouts ----------------------------- */

/**
 * What this workspace may build a floor from.
 *
 * `builtins` comes from the server rather than from the industry rule in the
 * browser: a platform administrator can now offer or withhold a built-in
 * layout directly, and a picker that worked the list out for itself would
 * offer layouts the create call then refuses.
 */
export const useSpaceTemplates = (enabled = true) =>
  useQuery({
    queryKey: ['space-templates'],
    queryFn: () =>
      api.get<List<SavedSpaceTemplate> & { builtins: SpaceTemplateName[] }>(
        '/spaces/templates',
      ),
    enabled,
    staleTime: 60_000,
  });

export function useSaveSpaceTemplate(spaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      api.post<SavedSpaceTemplate>(`/spaces/${spaceId}/template`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['space-templates'] }),
  });
}

export function useDeleteSpaceTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => api.delete(`/spaces/templates/${templateId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['space-templates'] }),
  });
}

export function useDeleteSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/spaces/${id}`),
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: ['space', id] });
      qc.removeQueries({ queryKey: ['space-members', id] });
      void qc.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

/* --------------------------- space members ------------------------------ */

export const useSpaceMembers = (id?: string) =>
  useQuery({
    queryKey: ['space-members', id],
    queryFn: () => api.get<List<SpaceMember>>(`/spaces/${id}/members`),
    enabled: Boolean(id),
  });

function useInvalidateSpaceMembers(id: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['space-members', id] });
    // The roster is embedded in the space document, so list/detail copies
    // of the space are stale too.
    qc.invalidateQueries({ queryKey: ['spaces'] });
    qc.invalidateQueries({ queryKey: ['space', id] });
  };
}

export function useAddSpaceMember(id: string) {
  const invalidate = useInvalidateSpaceMembers(id);
  return useMutation({
    mutationFn: (body: { userId: string; zoneId?: string; furnitureId?: string }) =>
      api.post<SpaceMember>(`/spaces/${id}/members`, body),
    onSuccess: invalidate,
  });
}

export function useAssignSpaceMemberWorkspace(id: string) {
  const invalidate = useInvalidateSpaceMembers(id);
  return useMutation({
    mutationFn: ({
      userId,
      ...assignment
    }: {
      userId: string;
      zoneId?: string | null;
      furnitureId?: string | null;
    }) => api.patch<SpaceMember>(`/spaces/${id}/members/${userId}`, assignment),
    onSuccess: invalidate,
  });
}

export function useRemoveSpaceMember(id: string) {
  const invalidate = useInvalidateSpaceMembers(id);
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/spaces/${id}/members/${userId}`),
    onSuccess: invalidate,
  });
}

/* ------------------------------ projects ------------------------------- */

export const useProjects = (enabled = true, includeArchived = false) =>
  useQuery({
    queryKey: ['projects', { includeArchived }],
    queryFn: () =>
      api.get<List<ProjectWithStats>>(`/projects${includeArchived ? '?archived=true' : ''}`),
    enabled,
  });

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      key?: string;
      description?: string;
      color?: string;
      /** Who the board is for. Anyone left out cannot see it unless they are
       *  an admin or end up holding a task on it. */
      memberIds?: string[];
      /** Yours alone — no members, and invisible to admins. Cannot be changed later. */
      personal?: boolean;
    }) => api.post<Project>('/projects', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

/**
 * The caller's private board, created the first time it is asked for. Safe to
 * call whenever a personal task needs somewhere to go — the server returns
 * the same board every time.
 */
export function useEnsurePersonalProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Project>('/projects/personal'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

/** One project on its own — the board holds the list, the task detail
 *  panel needs the full document for its custom field definitions. */
export const useProject = (id?: string) =>
  useQuery({
    queryKey: ['project', id],
    queryFn: () => api.get<Project>(`/projects/${id}`),
    enabled: Boolean(id),
  });

/* ----------------------------- milestones ------------------------------- */

export const useMilestones = (projectId?: string) =>
  useQuery({
    queryKey: ['milestones', projectId],
    queryFn: () => api.get<List<Milestone>>(`/projects/${projectId}/milestones`),
    enabled: Boolean(projectId),
  });

export function useCreateMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; description?: string; dueDate?: string }) =>
      api.post<Milestone>(`/projects/${projectId}/milestones`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['milestones', projectId] }),
  });
}

export function useUpdateMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch<Milestone>(`/projects/${projectId}/milestones/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['milestones', projectId] }),
  });
}

export function useDeleteMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${projectId}/milestones/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['milestones', projectId] });
      // Deleting detaches tasks server-side; their cached copies still point at it.
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/* -------------------------------- tasks -------------------------------- */

export const useTasks = (params: Record<string, string | undefined> = {}) => {
  const search = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v) as [string, string][],
  ).toString();
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: () => api.get<List<Task>>(`/tasks${search ? `?${search}` : ''}`),
  });
};

/**
 * Open work by default; `done` asks for what you finished instead. The two
 * are cached apart so switching to the completed view does not throw away the
 * list you came from — and `enabled` keeps the done half off the wire until
 * someone actually asks for it.
 */
export const useMyTasks = (done = false, enabled = true) =>
  useQuery({
    queryKey: ['tasks', 'mine', done ? 'done' : 'open'],
    queryFn: () => api.get<List<Task>>(`/tasks/mine${done ? '?done=true' : ''}`),
    enabled,
  });

export const useTask = (id?: string) =>
  useQuery({
    queryKey: ['task', id],
    queryFn: () => api.get<Task>(`/tasks/${id}`),
    enabled: Boolean(id),
  });

/**
 * Tasks offered by the `#` picker in a comment box.
 *
 * With nothing typed yet it lists this task's own project, which is where a
 * reference almost always points. Once there is a query it searches every
 * project the caller can open, because the interesting references are the
 * ones that cross a board.
 */
export const useTaskMentionSearch = (query: string | null, projectId?: string) => {
  const params: Record<string, string> = { limit: '8' };
  if (query) params.q = query;
  else if (projectId) params.projectId = projectId;
  const search = new URLSearchParams(params).toString();
  return useQuery({
    queryKey: ['tasks', 'mention', params],
    queryFn: () => api.get<List<Task>>(`/tasks?${search}`),
    enabled: query !== null && Boolean(query || projectId),
    staleTime: 30_000,
  });
};

export const useTaskComments = (id?: string) =>
  useQuery({
    queryKey: ['task', id, 'comments'],
    queryFn: () => api.get<List<TaskComment>>(`/tasks/${id}/comments`),
    enabled: Boolean(id),
  });

/** The task's change history — one row per edited field, oldest first. */
export const useTaskActivity = (id?: string) =>
  useQuery({
    queryKey: ['task', id, 'activity'],
    queryFn: () => api.get<List<Activity>>(`/tasks/${id}/activity`),
    enabled: Boolean(id),
  });

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Task>('/tasks', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch<Task>(`/tasks/${id}`, body),
    onSuccess: (task) => {
      qc.setQueryData(['task', task.id], task);
      qc.invalidateQueries({ queryKey: ['tasks'] });
      // Every saved edit may have grown the timeline.
      qc.invalidateQueries({ queryKey: ['task', task.id, 'activity'] });
      qc.invalidateQueries({ queryKey: ['milestones', task.projectId] });
    },
    // Every caller fires this and forgets it, so a rejected edit — a blocked
    // status change, a permission refusal — has to surface here or the field
    // just silently snaps back.
    onError: (err) =>
      toast.error('Could not save that change', err instanceof Error ? err.message : undefined),
  });
}

/**
 * "I have done my part", on a task each assignee completes separately.
 *
 * Kept apart from `useUpdateTask` because it writes only the caller's own row:
 * an assignee with no edit rights over the task still has to be able to say
 * they finished the training, and nobody here is speaking for the group.
 */
export function useSetMyTaskCompletion(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (done: boolean) =>
      done
        ? api.post<Task>(`/tasks/${taskId}/complete`)
        : api.delete<Task>(`/tasks/${taskId}/complete`),
    onSuccess: (task) => {
      qc.setQueryData(['task', task.id], task);
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task', task.id, 'activity'] });
    },
    onError: (err) =>
      toast.error('Could not save that', err instanceof Error ? err.message : undefined),
  });
}

/**
 * The same tick, for screens that hold many tasks at once.
 *
 * A board cannot mount a hook per card, and the card to tick is only known
 * once one is dropped — so the task id travels with the call rather than
 * being bound at render time.
 */
export function useSetTaskCompletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, done }: { taskId: string; done: boolean }) =>
      done
        ? api.post<Task>(`/tasks/${taskId}/complete`)
        : api.delete<Task>(`/tasks/${taskId}/complete`),
    onSuccess: (task) => {
      qc.setQueryData(['task', task.id], task);
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task', task.id, 'activity'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err) =>
      toast.error('Could not save that', err instanceof Error ? err.message : undefined),
  });
}

/* ----------------------------- attachments ------------------------------ */

/**
 * A task's files.
 *
 * A task carries `attachmentIds` but the API returns them unresolved, and
 * there is no fetch-by-ids endpoint. Scoping the upload to a per-task folder
 * means the existing listing endpoint can retrieve them as they are, with no
 * server change — the same arrangement the mobile client uses, so both see
 * the same files.
 */
const taskFolder = (taskId: string) => `/tasks/${taskId}`;

export const useTaskFiles = (taskId?: string) =>
  useQuery({
    queryKey: ['task', taskId, 'files'],
    queryFn: () =>
      api.get<List<FileAsset>>(`/files?folder=${encodeURIComponent(taskFolder(taskId!))}`),
    enabled: Boolean(taskId),
  });

export function useUploadTaskFiles(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]) => {
      const form = new FormData();
      for (const file of files) form.append('files', file);
      form.append('folder', taskFolder(taskId));
      const result = await api.upload<List<FileAsset>>('/files', form);

      // Keep the task record in step so the count is right elsewhere.
      const task = qc.getQueryData<Task>(['task', taskId]);
      await api.patch(`/tasks/${taskId}`, {
        attachmentIds: [...(task?.attachmentIds ?? []), ...result.items.map((f) => f.id)],
      });
      return result.items;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task', taskId, 'files'] });
      void qc.invalidateQueries({ queryKey: ['task', taskId] });
      void qc.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (err) =>
      toast.error("Couldn't attach that file", err instanceof Error ? err.message : undefined),
  });
}

/** Takes a file off a task and deletes it — a task folder is not a library. */
export function useRemoveTaskFile(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: string) => {
      await api.delete(`/files/${fileId}`);
      const task = qc.getQueryData<Task>(['task', taskId]);
      if (task?.attachmentIds?.length)
        await api.patch(`/tasks/${taskId}`, {
          attachmentIds: task.attachmentIds.filter((id) => id !== fileId),
        });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task', taskId, 'files'] });
      void qc.invalidateQueries({ queryKey: ['task', taskId] });
      void qc.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (err) =>
      toast.error("Couldn't remove that file", err instanceof Error ? err.message : undefined),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

/**
 * The shape a client hands back to the server for one uploaded file. Kept
 * beside the upload rather than in the component: comments carry the file
 * itself, the way chat messages do, not a reference to be resolved later.
 */
function asAttachment(asset: FileAsset): MessageAttachment {
  return {
    id: asset.id,
    name: asset.name,
    url: asset.url,
    mimeType: asset.mimeType,
    size: asset.size,
    ...(asset.width ? { width: asset.width } : {}),
    ...(asset.height ? { height: asset.height } : {}),
    ...(asset.blurhash ? { blurhash: asset.blurhash } : {}),
  };
}

/**
 * Files posted with a comment. They land in the task's own folder like every
 * other attachment — so they show up on mobile and in the library — but they
 * are not added to the task's attachment list: they belong to the remark.
 */
export function useUploadCommentFiles(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]): Promise<MessageAttachment[]> => {
      const form = new FormData();
      for (const file of files) form.append('files', file);
      form.append('folder', taskFolder(taskId));
      const result = await api.upload<List<FileAsset>>('/files', form);
      return result.items.map(asAttachment);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['task', taskId, 'files'] }),
  });
}

export function useAddComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, attachments }: { body: string; attachments?: MessageAttachment[] }) =>
      api.post<TaskComment>(`/tasks/${taskId}/comments`, { body, attachments }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task', taskId, 'comments'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/* ------------------------------ workflow -------------------------------- */

export const useDependencies = (taskId?: string) =>
  useQuery({
    queryKey: ['task', taskId, 'dependencies'],
    queryFn: () =>
      api.get<{ blockedBy: Task[]; blocks: Task[] }>(`/tasks/${taskId}/dependencies`),
    enabled: Boolean(taskId),
  });

export function useAddDependency(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockerId: string) =>
      api.post<Task>(`/tasks/${taskId}/dependencies`, { blockerId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task', taskId] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useRemoveDependency(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockerId: string) => api.delete(`/tasks/${taskId}/dependencies/${blockerId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task', taskId] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export const useSubtasks = (taskId?: string) =>
  useQuery({
    queryKey: ['task', taskId, 'subtasks'],
    queryFn: () => api.get<List<Task>>(`/tasks/${taskId}/subtasks`),
    enabled: Boolean(taskId),
  });

export const useTimeEntries = (taskId?: string) =>
  useQuery({
    queryKey: ['task', taskId, 'time'],
    queryFn: () => api.get<List<TimeEntry> & { totalMinutes: number }>(`/tasks/${taskId}/time`),
    enabled: Boolean(taskId),
  });

export function useLogTime(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { minutes: number; note?: string }) =>
      api.post<Task>(`/tasks/${taskId}/time`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task', taskId] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/* ----------------------------- automations ------------------------------ */

type AutomationWithAuthor = Automation & { createdByUser?: PublicUser };

export const useAutomations = (params: { scope?: string; spaceId?: string } = {}) =>
  useQuery({
    queryKey: ['automations', params.scope ?? 'all', params.spaceId ?? ''],
    queryFn: () => {
      const query = new URLSearchParams();
      // A floor asks by id, which already implies the scope; everything else
      // asks by scope, or for the lot.
      if (params.spaceId) query.set('spaceId', params.spaceId);
      else if (params.scope) query.set('scope', params.scope);
      const suffix = query.toString();
      return api.get<List<AutomationWithAuthor>>(`/automations${suffix ? `?${suffix}` : ''}`);
    },
  });

export const useAutomationRuns = (id?: string) =>
  useQuery({
    queryKey: ['automations', id, 'runs'],
    queryFn: () => api.get<List<AutomationRun>>(`/automations/${id}/runs`),
    enabled: Boolean(id),
  });

export function useCreateAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Automation>('/automations', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
  });
}

export function useUpdateAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch<Automation>(`/automations/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
  });
}

export function useDeleteAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/automations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
  });
}

export function useTestAutomation() {
  return useMutation({
    // A task rule is tried against a task; a floor rule against a room. The
    // route reads whichever of the two the rule's scope calls for.
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      taskId?: string;
      spaceId?: string;
      zoneId?: string;
    }) =>
      api.post<{ ran: boolean; appliedActions: string[]; message: string }>(
        `/automations/${id}/test`,
        body,
      ),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch<Project>(`/projects/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

/* ------------------------------ channels ------------------------------- */

export const useChannels = (enabled = true) =>
  useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<List<ChannelSummary>>('/channels'),
    enabled,
  });

export const useStories = () =>
  useQuery({
    queryKey: ['stories'],
    queryFn: () => api.get<List<ChatStory>>('/stories'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

export function useCreateStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { media: MessageAttachment; caption?: string }) =>
      api.post<ChatStory>('/stories', body),
    onSuccess: (story) =>
      qc.setQueryData<List<ChatStory>>(['stories'], (current) => ({
        items: [...(current?.items ?? []), story],
      })),
  });
}

/** Marks a story watched — grays its ring on the rail. Idempotent. */
export function useMarkStorySeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storyId: string) => api.post(`/stories/${storyId}/view`),
    onMutate: (storyId) =>
      qc.setQueryData<List<ChatStory>>(['stories'], (current) => ({
        items: (current?.items ?? []).map((story) =>
          story.id === storyId && !story.seen ? { ...story, seen: true } : story,
        ),
      })),
  });
}

export function useDeleteStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storyId: string) => api.delete(`/stories/${storyId}`),
    onSuccess: (_result, storyId) =>
      qc.setQueryData<List<ChatStory>>(['stories'], (current) => ({
        items: (current?.items ?? []).filter((story) => story.id !== storyId),
      })),
  });
}

/**
 * Leaves, changes or takes back a reaction on a story.
 *
 * Applied optimistically and reconciled against the server's copy: the emoji
 * has to land under your thumb before the story moves on, and one round trip
 * is longer than some stories are on screen.
 */
export function useReactToStory() {
  const qc = useQueryClient();
  const patch = (storyId: string, next: ChatStory | ((story: ChatStory) => ChatStory)) =>
    qc.setQueryData<List<ChatStory>>(['stories'], (current) => ({
      items: (current?.items ?? []).map((story) =>
        story.id === storyId ? (typeof next === 'function' ? next(story) : next) : story,
      ),
    }));

  return useMutation({
    mutationFn: ({ storyId, emoji }: { storyId: string; emoji: string }) =>
      api.post<ChatStory>(`/stories/${storyId}/react`, { emoji }),
    onMutate: ({ storyId, emoji }) =>
      patch(storyId, (story) => {
        const mine = story.reactions.find((reaction) => reaction.mine);
        const removing = mine?.emoji === emoji;
        const reactions = story.reactions
          .map((reaction) =>
            reaction.mine ? { ...reaction, count: reaction.count - 1, mine: false } : reaction,
          )
          .filter((reaction) => reaction.count > 0);
        if (removing) return { ...story, reactions, seen: true };
        const existing = reactions.find((reaction) => reaction.emoji === emoji);
        return {
          ...story,
          seen: true,
          reactions: existing
            ? reactions.map((reaction) =>
                reaction.emoji === emoji
                  ? { ...reaction, count: reaction.count + 1, mine: true }
                  : reaction,
              )
            : [...reactions, { emoji, count: 1, mine: true }],
        };
      }),
    onSuccess: (story) => patch(story.id, story),
    // The rail polls every minute, so a failed reaction corrects itself.
    onError: () => qc.invalidateQueries({ queryKey: ['stories'] }),
  });
}

/**
 * Replies to a story, which sends a DM to its author.
 *
 * The reply lands in a real conversation, so the channel list and that DM's
 * transcript are both refreshed — otherwise the message only appears once
 * something else refetched.
 */
export function useReplyToStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ storyId, body }: { storyId: string; body: string }) =>
      api.post<{ channelId: string; message: Message }>(`/stories/${storyId}/reply`, { body }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      void qc.invalidateQueries({ queryKey: ['messages', result.channelId] });
      void qc.invalidateQueries({ queryKey: ['stories'] });
    },
  });
}

/**
 * A page of transcript. `hasNewer` is set only when the page came back from
 * `?around=`, and means the client is holding a slice out of the middle of the
 * channel rather than its live end.
 */
export type MessagePage = List<Message> & { hasNewer?: boolean; focusMessageId?: string };

export const useMessages = (channelId?: string) =>
  useQuery({
    queryKey: ['messages', channelId],
    queryFn: () => api.get<MessagePage>(`/channels/${channelId}/messages`),
    enabled: Boolean(channelId),
  });

/**
 * Walks back through history a page at a time.
 *
 * Deliberately not `useInfiniteQuery`: the optimistic paths for sending,
 * reacting and deleting all read and write `{ items }` at this key, and a
 * paged cache would mean rewriting every one of them. Prepending into the
 * single flat list keeps them untouched. Mirrors the mobile client.
 */
export function useOlderMessages(channelId?: string) {
  const qc = useQueryClient();
  const key = ['messages', channelId];
  const cursor = qc.getQueryData<MessagePage>(key)?.nextCursor;

  const mutation = useMutation({
    mutationFn: async () => {
      if (!channelId || !cursor) return null;
      return api.get<MessagePage>(
        `/channels/${channelId}/messages?before=${encodeURIComponent(cursor)}`,
      );
    },
    onSuccess: (page) => {
      if (!page) return;
      qc.setQueryData<MessagePage>(key, (cached) => {
        if (!cached) return cached;
        // The socket may have delivered one of these already.
        const known = new Set(cached.items.map((message) => message.id));
        const older = page.items.filter((message) => !known.has(message.id));
        return { ...cached, items: [...older, ...cached.items], nextCursor: page.nextCursor };
      });
    },
  });

  return {
    hasMore: Boolean(cursor),
    loadOlder: mutation.mutateAsync,
    loading: mutation.isPending,
  };
}

/**
 * Loads the transcript around one message, replacing whatever page is cached.
 *
 * This is what makes a link to a message mean anything — a notification, a
 * search hit, a pin, a saved message. Each of those names a message that may
 * be well outside the newest page, and every one of them previously failed
 * with "scroll up to load it".
 */
export function useMessageContext(channelId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      api.get<MessagePage>(
        `/channels/${channelId}/messages?around=${encodeURIComponent(messageId)}`,
      ),
    onSuccess: (page) => qc.setQueryData<MessagePage>(['messages', channelId], page),
  });
}

/** Drops back to the live end of the channel after reading somewhere older. */
export function useLatestMessages(channelId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.get<MessagePage>(`/channels/${channelId}/messages`),
    onSuccess: (page) => qc.setQueryData<MessagePage>(['messages', channelId], page),
  });
}

/**
 * Rewrites a message you already sent. The server keeps the previous body, so
 * "(edited)" always has a history behind it.
 */
export function useEditMessage(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, body }: { messageId: string; body: string }) =>
      api.patch<Message>(`/channels/${channelId}/messages/${messageId}`, { body }),
    /* The socket broadcasts `message:updated` to the room, but the author's own
       view should not wait on a round trip through it. */
    onSuccess: (message) =>
      qc.setQueryData<MessagePage>(['messages', channelId], (current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) => (item.id === message.id ? message : item)),
            }
          : current,
      ),
  });
}

/**
 * The channel's live call. The socket's `channel:call` events keep this cache
 * current while the conversation is open; the query answers the moment it
 * opens, so arriving mid-call shows the banner immediately.
 */
export const useChannelCall = (channelId?: string) =>
  useQuery({
    queryKey: ['channel-call', channelId],
    queryFn: () => api.get<{ call: ActiveCall | null }>(`/channels/${channelId}/call`),
    enabled: Boolean(channelId),
    staleTime: 15_000,
  });

export const useThread = (channelId?: string, messageId?: string) =>
  useQuery({
    queryKey: ['thread', channelId, messageId],
    queryFn: () =>
      api.get<List<Message> & { following: boolean }>(
        `/channels/${channelId}/threads/${messageId}`,
      ),
    enabled: Boolean(channelId && messageId),
  });

/* The optimistic sidebar preview is `messagePreview` from the shared package —
   the same function the server writes to `lastMessagePreview` — so the row does
   not change wording underneath the reader when the real message lands. */

export function useSendMessage(channelId: string) {
  const qc = useQueryClient();
  const { user, orgId } = useAuth();
  return useMutation({
    mutationFn: (body: {
      body: string;
      parentId?: string;
      attachments?: unknown[];
      poll?: PollInput;
      quotedMessageId?: string;
      /** Diverts the message into a scheduled record instead of posting it. */
      sendAt?: string;
    }) => api.post<Message>(`/channels/${channelId}/messages`, body),
    onMutate: async (vars) => {
      // Polls and scheduled sends skip the optimistic path: one would have its
      // guessed option ids thrown away, and the other is not in the transcript.
      if (vars.parentId || vars.poll || vars.sendAt || !user) return undefined;
      await qc.cancelQueries({ queryKey: ['messages', channelId] });
      const previous = qc.getQueryData<List<Message>>(['messages', channelId]);
      const optimistic: Message = {
        id: `pending-${crypto.randomUUID()}`,
        orgId: orgId ?? '',
        channelId,
        author: user,
        body: vars.body,
        replyCount: 0,
        mentions: [],
        attachments: (vars.attachments ?? []) as Message['attachments'],
        reactions: [],
        createdAt: new Date().toISOString(),
      };
      qc.setQueryData<List<Message>>(['messages', channelId], (current) => ({
        ...(current ?? { items: [] }),
        items: [...(current?.items ?? []), optimistic],
      }));
      return { previous, optimisticId: optimistic.id };
    },
    onSuccess: (msg, vars, context) => {
      if (vars.sendAt) {
        // It went into the scheduled list, not the transcript.
        void qc.invalidateQueries({ queryKey: ['inbox', 'scheduled'] });
        return;
      }
      if (!vars.parentId) {
        qc.setQueryData<List<Message>>(['messages', channelId], (current) =>
          current
            ? {
                ...current,
                items: current.items.some((item) => item.id === msg.id)
                  ? current.items.filter((item) => item.id !== context?.optimisticId)
                  : current.items.map((item) =>
                      item.id === context?.optimisticId ? msg : item,
                    ),
              }
            : current,
        );
      }
      if (vars.parentId)
        void qc.invalidateQueries({ queryKey: ['thread', channelId, vars.parentId] });
      else {
        const preview = messagePreview(msg);
        qc.setQueryData<List<ChannelSummary>>(['channels'], (current) =>
          current
            ? {
                ...current,
                items: current.items.map((item) =>
                  item.id === channelId
                    ? {
                        ...item,
                        lastMessageAt: msg.createdAt,
                        lastMessagePreview: preview,
                      }
                    : item,
                ),
              }
            : current,
        );
      }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) qc.setQueryData(['messages', channelId], context.previous);
      else if (context?.optimisticId) {
        qc.setQueryData<List<Message>>(['messages', channelId], (current) =>
          current
            ? {
                ...current,
                items: current.items.filter((item) => item.id !== context.optimisticId),
              }
            : current,
        );
      }
    },
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; topic?: string; isPrivate: boolean }) =>
      api.post<Channel>('/channels', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useOpenDm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: string[] | { userIds: string[]; name?: string }) =>
      api.post<Channel>('/channels/dm', Array.isArray(input) ? { userIds: input } : input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useInviteChannelMembers(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) =>
      api.post<Channel>(`/channels/${channelId}/members`, { userIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useMarkChannelRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) => api.post(`/channels/${channelId}/read`),
    onMutate: (channelId) => {
      qc.setQueryData<List<ChannelSummary>>(['channels'], (current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) =>
                item.id === channelId ? { ...item, unreadCount: 0, mentionCount: 0 } : item,
              ),
            }
          : current,
      );
    },
    // If the receipt failed, refresh once to restore the server's true count.
    onError: () => void qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

/**
 * How far everyone else has read this conversation. The server answers
 * `disabled` for a room too big for receipts, or when the reader has turned
 * their own off — in both cases the client shows no marker at all.
 */
export const useMessageReceipts = (channelId?: string) =>
  useQuery({
    queryKey: ['message-receipts', channelId],
    queryFn: () => api.get<ChannelReceipts>(`/channels/${channelId}/receipts`),
    enabled: Boolean(channelId),
  });

export function useToggleReaction(channelId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      api.post<ReactionResult>(`/channels/${channelId}/messages/${messageId}/reactions`, {
        emoji,
      }),
    onMutate: async ({ messageId, emoji }) => {
      if (!user) return undefined;
      await qc.cancelQueries({ queryKey: ['messages', channelId] });
      const current = qc.getQueryData<List<Message>>(['messages', channelId]);
      const previousReactions = current?.items.find(
        (message) => message.id === messageId,
      )?.reactions;

      qc.setQueryData<List<Message>>(['messages', channelId], (cached) => {
        if (!cached) return cached;
        return {
          ...cached,
          items: cached.items.map((message) => {
            if (message.id !== messageId) return message;
            const existing = message.reactions.find((reaction) => reaction.emoji === emoji);
            const reacted = existing?.userIds.includes(user.id) ?? false;
            const reactions = existing
              ? reacted
                ? existing.userIds.length === 1
                  ? message.reactions.filter((reaction) => reaction.emoji !== emoji)
                  : message.reactions.map((reaction) =>
                      reaction.emoji === emoji
                        ? {
                            ...reaction,
                            userIds: reaction.userIds.filter((id) => id !== user.id),
                          }
                        : reaction,
                    )
                : message.reactions.map((reaction) =>
                    reaction.emoji === emoji
                      ? { ...reaction, userIds: [...reaction.userIds, user.id] }
                      : reaction,
                  )
              : [...message.reactions, { emoji, userIds: [user.id] }];
            return { ...message, reactions };
          }),
        };
      });
      return { messageId, previousReactions };
    },
    onSuccess: ({ kudos, ...updated }, { emoji }) => {
      qc.setQueryData<List<Message>>(['messages', channelId], (cached) =>
        cached
          ? {
              ...cached,
              items: cached.items.map((message) =>
                message.id === updated.id ? updated : message,
              ),
            }
          : cached,
      );
      // The workspace's Appy emoji did more than react. Say so — an Appy
      // spent without a word is one the giver did not know they gave — and
      // refresh the giver's allowance, which the Appy wall reads.
      if (kudos === 'given') {
        toast.success(`You gave ${updated.author.name} an Appy ${emoji}`);
        void qc.invalidateQueries({ queryKey: ['engagement', 'me'] });
      } else if (kudos === 'limit') {
        toast.info(
          'You are out of Appys for today',
          'The reaction stayed; tomorrow brings more.',
        );
      }
    },
    onError: (_error, _variables, context) => {
      if (!context?.previousReactions) return;
      const previousReactions = context.previousReactions;
      qc.setQueryData<List<Message>>(['messages', channelId], (cached) =>
        cached
          ? {
              ...cached,
              items: cached.items.map((message) =>
                message.id === context.messageId
                  ? { ...message, reactions: previousReactions }
                  : message,
              ),
            }
          : cached,
      );
    },
  });
}

export function useDeleteMessage(channelId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (messageId: string) =>
      api.delete(`/channels/${channelId}/messages/${messageId}`),
    onSuccess: (_result, messageId) => {
      // The row stays as a tombstone. The socket broadcast carries the
      // authoritative one; this is just so the deleter sees it immediately.
      qc.setQueryData<List<Message>>(['messages', channelId], (current) =>
        current
          ? {
              ...current,
              items: current.items.map((message) =>
                message.id === messageId
                  ? {
                      ...message,
                      body: '',
                      attachments: [],
                      reactions: [],
                      poll: undefined,
                      pinnedAt: undefined,
                      deletedAt: new Date().toISOString(),
                      deletedBy: user ?? undefined,
                    }
                  : message,
              ),
            }
          : current,
      );
      void qc.invalidateQueries({ queryKey: ['channels'] });
      void qc.invalidateQueries({ queryKey: ['pins', channelId] });
    },
  });
}

/* --------------------------- pins and polls ------------------------------ */

export const usePins = (channelId?: string) =>
  useQuery({
    queryKey: ['pins', channelId],
    queryFn: () => api.get<List<Message>>(`/channels/${channelId}/pins`),
    enabled: Boolean(channelId),
  });

export function useTogglePin(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, pinned }: { messageId: string; pinned: boolean }) =>
      pinned
        ? api.delete<Message>(`/channels/${channelId}/messages/${messageId}/pin`)
        : api.post<Message>(`/channels/${channelId}/messages/${messageId}/pin`, {}),
    onSuccess: (updated) => {
      if (updated) replaceMessage(qc, channelId, updated);
      void qc.invalidateQueries({ queryKey: ['pins', channelId] });
    },
  });
}

export function useVotePoll(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, optionIds }: { messageId: string; optionIds: string[] }) =>
      api.post<Message>(`/channels/${channelId}/messages/${messageId}/vote`, { optionIds }),
    onSuccess: (updated) => replaceMessage(qc, channelId, updated),
  });
}

export function useClosePoll(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      api.post<Message>(`/channels/${channelId}/messages/${messageId}/poll/close`, {}),
    onSuccess: (updated) => replaceMessage(qc, channelId, updated),
  });
}

/** Hands the browser's own transcript to the server after a voice note lands. */
export function useAttachTranscript(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      attachmentId,
      transcript,
    }: {
      messageId: string;
      attachmentId: string;
      transcript: string;
    }) =>
      api.post<Message>(`/channels/${channelId}/messages/${messageId}/transcript`, {
        attachmentId,
        transcript,
      }),
    onSuccess: (updated) => replaceMessage(qc, channelId, updated),
  });
}

/** Asks the server to transcribe — for browsers that cannot do it themselves. */
export function useTranscribeVoiceNote(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, attachmentId }: { messageId: string; attachmentId: string }) =>
      api.post<Message>(
        `/channels/${channelId}/messages/${messageId}/transcribe?attachmentId=${attachmentId}`,
        {},
      ),
    onSuccess: (updated) => replaceMessage(qc, channelId, updated),
  });
}

function replaceMessage(
  qc: ReturnType<typeof useQueryClient>,
  channelId: string,
  updated: Message,
): void {
  qc.setQueryData<List<Message>>(['messages', channelId], (cached) =>
    cached
      ? {
          ...cached,
          items: cached.items.map((message) => (message.id === updated.id ? updated : message)),
        }
      : cached,
  );
}

/* ----------------------- message actions and inbox ----------------------- */

/** Earlier versions of a message, fetched only when the reader asks. */
export const useMessageEdits = (channelId: string, messageId?: string) =>
  useQuery({
    queryKey: ['edits', channelId, messageId],
    queryFn: () => api.get<Message>(`/channels/${channelId}/messages/${messageId}/edits`),
    enabled: Boolean(messageId),
  });

export function useSaveMessage(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      saved,
      note,
    }: {
      messageId: string;
      saved: boolean;
      note?: string;
    }) =>
      saved
        ? api.delete(`/channels/${channelId}/messages/${messageId}/save`)
        : api.post(`/channels/${channelId}/messages/${messageId}/save`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox', 'saved'] }),
  });
}

export function useRemindMe(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, remindAt }: { messageId: string; remindAt: string }) =>
      api.post(`/channels/${channelId}/messages/${messageId}/remind`, { remindAt }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox', 'reminders'] }),
  });
}

export function useMarkUnreadFrom(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      api.post<{ unreadCount: number; mentionCount: number; from: string }>(
        `/channels/${channelId}/messages/${messageId}/unread`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useForwardMessage(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      channelIds,
      note,
    }: {
      messageId: string;
      channelIds: string[];
      note?: string;
    }) =>
      api.post<{ messageIds: string[] }>(
        `/channels/${channelId}/messages/${messageId}/forward`,
        { channelIds, note },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useMessageToTask(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      projectId,
      title,
      assigneeIds,
    }: {
      messageId: string;
      projectId: string;
      title?: string;
      assigneeIds?: string[];
    }) =>
      api.post<Task>(`/channels/${channelId}/messages/${messageId}/task`, {
        projectId,
        title,
        assigneeIds,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useFollowThread(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, following }: { messageId: string; following: boolean }) =>
      following
        ? api.delete(`/channels/${channelId}/messages/${messageId}/follow`)
        : api.post(`/channels/${channelId}/messages/${messageId}/follow`),
    onSuccess: (_data, { messageId }) => {
      // The thread query carries `following`, so the toggle reads its state
      // back from the server rather than trusting its own click.
      void qc.invalidateQueries({ queryKey: ['thread', channelId, messageId] });
      void qc.invalidateQueries({ queryKey: ['inbox', 'threads'] });
    },
  });
}

export const useSavedMessages = () =>
  useQuery({
    queryKey: ['inbox', 'saved'],
    queryFn: () => api.get<List<SavedMessage>>('/inbox/saved'),
  });

export const useReminders = () =>
  useQuery({
    queryKey: ['inbox', 'reminders'],
    queryFn: () => api.get<List<MessageReminder>>('/inbox/reminders'),
  });

/* ------------------------------ webhooks -------------------------------- */

export const useIncomingWebhooks = (enabled = true) =>
  useQuery({
    queryKey: ['webhooks', 'incoming'],
    queryFn: () => api.get<List<IncomingWebhook>>('/webhooks/incoming'),
    enabled,
  });

export const useOutgoingWebhooks = (enabled = true) =>
  useQuery({
    queryKey: ['webhooks', 'outgoing'],
    queryFn: () => api.get<List<OutgoingWebhook>>('/webhooks/outgoing'),
    enabled,
  });

export const useThreadInbox = () =>
  useQuery({
    queryKey: ['inbox', 'threads'],
    queryFn: () => api.get<List<ThreadSummary>>('/inbox/threads'),
    refetchInterval: 60_000,
  });

export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.post(`/inbox/threads/${messageId}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox', 'threads'] }),
  });
}

export const useScheduledMessages = () =>
  useQuery({
    queryKey: ['inbox', 'scheduled'],
    queryFn: () => api.get<List<ScheduledMessage>>('/inbox/scheduled'),
  });

export function useCancelScheduled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/inbox/scheduled/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox', 'scheduled'] }),
  });
}

export function useDeleteReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/inbox/reminders/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox', 'reminders'] }),
  });
}

/* ------------------------- reminders you write --------------------------- */

/**
 * The reminders someone wrote for themselves, personal and work in one list.
 *
 * A separate key from `['inbox', 'reminders']`: that one is message reminders,
 * and the two are different shapes from different routes even though both are
 * "reminders" to the person reading them.
 */
export const useMyReminders = () =>
  useQuery({
    queryKey: ['reminders'],
    queryFn: () => api.get<List<Reminder>>('/reminders'),
  });

export function useCreateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      note?: string;
      category: ReminderCategory;
      remindAt: string;
    }) => api.post<Reminder>('/reminders', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders'] }),
  });
}

export function useUpdateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title?: string;
      note?: string | null;
      category?: ReminderCategory;
      remindAt?: string;
      done?: boolean;
    }) => api.patch<Reminder>(`/reminders/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders'] }),
  });
}

export function useDeleteMyReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/reminders/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders'] }),
  });
}

/* -------------------------- channel preferences -------------------------- */

export function useChannelPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      ...body
    }: {
      channelId: string;
      level?: NotificationLevel;
      muted?: boolean;
      pinned?: boolean;
    }) => api.put(`/channels/${channelId}/prefs`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export const useNotificationSettings = () =>
  useQuery({
    queryKey: ['notificationSettings'],
    queryFn: () => api.get<NotificationSettings>('/users/me/notifications'),
  });

export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      keywords?: string[];
      mutedUserIds?: string[];
      quietHours?: { start: string; end: string; timezone?: string } | null;
      quietHoursMode?: QuietHoursMode;
      dnd?: { until: string } | null;
    }) => api.patch<NotificationSettings>('/users/me/notifications', body),
    onSuccess: (settings) => qc.setQueryData(['notificationSettings'], settings),
  });
}

/**
 * Pause notifications for `minutes`, or end the pause with `null`.
 *
 * The signed-in user carries `dndUntil` for the moon in the top bar, and the
 * settings query carries the same fact for the settings page; both are
 * written from the one response so they cannot disagree.
 */
export function useSetDoNotDisturb() {
  const qc = useQueryClient();
  const user = useAuth((state) => state.user);
  const setUser = useAuth((state) => state.setUser);
  return useMutation({
    mutationFn: (minutes: number | null) =>
      api.post<NotificationSettings>('/users/me/dnd', { minutes }),
    onSuccess: (settings) => {
      qc.setQueryData(['notificationSettings'], settings);
      if (user) setUser({ ...user, dndUntil: settings.dnd?.until });
    },
  });
}

/** Push a DM through the recipient's do-not-disturb. Once per message. */
export function useNotifyAnyway(channelId: string) {
  return useMutation({
    mutationFn: (messageId: string) =>
      api.post<void>(`/channels/${channelId}/messages/${messageId}/notify-anyway`),
  });
}

/* ------------------------ search, unfurls, bookmarks --------------------- */

export type SearchHit = Message & { channelName?: string };

/**
 * Message search, paged.
 *
 * The endpoint has always returned a `nextCursor`; the rail used to drop it,
 * so results stopped at the first page with nothing to say more existed. An
 * infinite query is safe here in a way it is not for the transcript — nothing
 * writes optimistically into these results.
 */
export const useMessageSearch = (query: string, channelId?: string) =>
  useInfiniteQuery({
    queryKey: ['messageSearch', query, channelId ?? null],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ q: query });
      if (channelId) params.set('channelId', channelId);
      if (pageParam) params.set('before', pageParam);
      return api.get<List<SearchHit>>(`/search/messages?${params}`);
    },
    getNextPageParam: (last) => last.nextCursor,
    enabled: query.trim().length > 1,
  });

/** A workspace link resolved into a live card, or a 404 the card hides behind. */
export const useInternalUnfurl = (path?: string) =>
  useQuery({
    queryKey: ['internalUnfurl', path],
    queryFn: () =>
      api.get<InternalUnfurl>(`/search/internal-unfurl?path=${encodeURIComponent(path!)}`),
    enabled: Boolean(path),
    // Short enough that a renamed task or ended meeting catches up, long
    // enough that scrolling a channel doesn't re-ask per message.
    staleTime: 60_000,
    retry: false,
  });

export const useLinkPreview = (url?: string) =>
  useQuery({
    queryKey: ['unfurl', url],
    queryFn: () => api.get<LinkPreview>(`/search/unfurl?url=${encodeURIComponent(url!)}`),
    enabled: Boolean(url),
    staleTime: 60 * 60_000,
    retry: false,
  });

export function useUpdateBookmarks(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookmarks: { id?: string; label: string; url: string; emoji?: string }[]) =>
      api.put<Channel>(`/channels/${channelId}/bookmarks`, { bookmarks }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export interface ChannelFile extends MessageAttachment {
  messageId: string;
  sharedBy?: PublicUser;
  sharedAt: string;
}

export const useChannelFiles = (channelId?: string) =>
  useQuery({
    queryKey: ['channelFiles', channelId],
    queryFn: () => api.get<List<ChannelFile>>(`/channels/${channelId}/files`),
    enabled: Boolean(channelId),
  });

/* --------------------- custom emoji, stickers, GIFs ---------------------- */

export const useCustomEmoji = () =>
  useQuery({
    queryKey: ['emoji'],
    queryFn: () => api.get<List<CustomEmoji>>('/emoji'),
    staleTime: 5 * 60_000,
  });

export function useUploadCustomEmoji() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, file, pack }: { name: string; file: File; pack?: string }) => {
      const form = new FormData();
      form.append('image', file);
      form.append('name', name);
      if (pack) form.append('pack', pack);
      return api.upload<CustomEmoji>('/emoji', form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emoji'] }),
  });
}

export function useDeleteCustomEmoji() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/emoji/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emoji'] }),
  });
}

export const useStickerPacks = () =>
  useQuery({
    queryKey: ['stickers'],
    queryFn: () => api.get<List<StickerPack>>('/emoji/stickers'),
    staleTime: 5 * 60_000,
  });

export function useCreateStickerPack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<StickerPack>('/emoji/stickers', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stickers'] }),
  });
}

/** Stickers arrive as one multipart upload so a whole pack lands in one go. */
export function useUploadStickers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packId, files }: { packId: string; files: File[] }) => {
      const form = new FormData();
      for (const file of files) form.append('images', file);
      return api.upload<StickerPack>(`/emoji/stickers/${packId}`, form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stickers'] }),
  });
}

export function useDeleteStickerPack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/emoji/stickers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stickers'] }),
  });
}

/**
 * GIF search. An empty query is the trending list rather than no request, so
 * an opened picker has something in it before anyone types.
 */
export const useGifSearch = (query: string, enabled: boolean) =>
  useQuery({
    queryKey: ['gifs', query],
    queryFn: () => api.get<List<GifResult>>(`/gifs?q=${encodeURIComponent(query)}`),
    enabled,
    staleTime: 5 * 60_000,
  });

/* -------------------------------- widgets -------------------------------- */

export const useWidgets = (surface: WidgetSurface, scopeId?: string, zoneId?: string) =>
  useQuery({
    queryKey: ['widgets', surface, scopeId ?? 'me', zoneId ?? null],
    queryFn: () => {
      const params = new URLSearchParams({ surface });
      if (scopeId) params.set('scopeId', scopeId);
      if (zoneId) params.set('zoneId', zoneId);
      return api.get<List<Widget>>(`/widgets?${params}`);
    },
    enabled:
      surface === 'home' || surface === 'dashboard' || surface === 'mobile' || Boolean(scopeId),
  });

export function useCreateWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      kind: WidgetKind;
      surface: WidgetSurface;
      scopeId?: string;
      zoneId?: string;
      title?: string;
      span?: 1 | 2 | 3 | 4;
      rows?: 1 | 2 | 3 | 4;
      config?: Record<string, unknown>;
    }) => api.post<Widget>('/widgets', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  });
}

/**
 * Every cached widget list, kept so a failed mutation can put them all back.
 *
 * Widgets are cached per surface and a mutation only knows its ids, so the
 * optimistic writers below run over every list and leave alone the ones that
 * do not hold what changed.
 */
type WidgetSnapshot = ReturnType<QueryClient['getQueriesData']>;

const widgetSnapshot = (qc: QueryClient): WidgetSnapshot =>
  qc.getQueriesData<List<Widget>>({ queryKey: ['widgets'] });

const restoreWidgets = (qc: QueryClient, snapshot?: WidgetSnapshot) => {
  for (const [key, data] of snapshot ?? []) qc.setQueryData(key, data);
};

export function useUpdateWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title?: string | null;
      span?: 1 | 2 | 3 | 4;
      rows?: 1 | 2 | 3 | 4;
      order?: number;
      config?: Record<string, unknown>;
    }) => api.patch<Widget>(`/widgets/${id}`, body),

    /* Applied to the cache before the request goes out: a section being
       resized under the cursor has to widen with it, and waiting for the round
       trip would snap it back to its old width for as long as that took. */
    onMutate: async ({ id, ...body }) => {
      await qc.cancelQueries({ queryKey: ['widgets'] });
      const snapshot = widgetSnapshot(qc);
      qc.setQueriesData<List<Widget>>({ queryKey: ['widgets'] }, (old) => {
        if (!old?.items.some((widget) => widget.id === id)) return old;
        return {
          ...old,
          items: old.items.map((widget) =>
            widget.id === id
              ? {
                  ...widget,
                  ...(body.span !== undefined && { span: body.span }),
                  ...(body.rows !== undefined && { rows: body.rows }),
                  ...(body.order !== undefined && { order: body.order }),
                  ...(body.title !== undefined && { title: body.title ?? undefined }),
                  /* Merged rather than replaced, which is what the server
                     does with a config patch. */
                  ...(body.config && { config: { ...widget.config, ...body.config } }),
                }
              : widget,
          ),
        };
      });
      return { snapshot };
    },
    onError: (_error, _body, context) => restoreWidgets(qc, context?.snapshot),
    onSettled: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  });
}

export function useDeleteWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/widgets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  });
}

export function useReorderWidgets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<List<Widget>>('/widgets/reorder', { ids }),

    /* A section dropped in its new place has to stay there. Without this the
       list re-renders from the old cache the moment the drag ends and the
       section jumps home until the refetch lands. */
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: ['widgets'] });
      const snapshot = widgetSnapshot(qc);
      qc.setQueriesData<List<Widget>>({ queryKey: ['widgets'] }, (old) => {
        if (!old) return old;
        const byId = new Map(old.items.map((widget) => [widget.id, widget]));
        // Some other surface's list; none of this reorder belongs to it.
        if (!ids.every((id) => byId.has(id))) return old;
        /* The list may be partial — the board hides sections whose feature is
           switched off and cannot reorder what it does not draw. Unlisted ones
           keep their relative order and settle after the rest, which is what
           the server does with the same list. */
        const listed = new Set(ids);
        const items = [
          ...ids.map((id) => byId.get(id)!),
          ...old.items.filter((widget) => !listed.has(widget.id)),
        ];
        return { ...old, items: items.map((widget, order) => ({ ...widget, order })) };
      });
      return { snapshot };
    },
    onError: (_error, _ids, context) => restoreWidgets(qc, context?.snapshot),
    onSettled: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  });
}

/** Puts home back to the default layout, or clears it. */
export function useApplyHomeTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (template: 'classic' | 'empty') =>
      api.post<List<Widget>>('/widgets/home/template', { template }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  });
}

/* ----------------------------- wall screens ----------------------------- */

/** Which prop on this floor shows what. Changes only when someone redecorates. */
export const useScreenWidgets = (spaceId?: string) =>
  useQuery({
    queryKey: ['screens', spaceId],
    queryFn: () => api.get<List<Widget>>(`/screens?spaceId=${spaceId}`),
    enabled: Boolean(spaceId),
  });

/**
 * The glance version of every screen, for painting onto the floor.
 *
 * Polled rather than pushed. What a wall says drifts constantly — a meeting
 * starts, somebody walks in, a countdown ticks over — and none of that is
 * worth a socket event of its own; the floor also refetches on the events
 * that plainly change it (see SpacePage).
 */
export const useScreenSnapshots = (spaceId?: string, enabled = true) =>
  useQuery({
    queryKey: ['screen-snapshots', spaceId],
    queryFn: () => api.get<List<ScreenSnapshot>>(`/screens/snapshots?spaceId=${spaceId}`),
    enabled: Boolean(spaceId) && enabled,
    refetchInterval: SCREEN_SNAPSHOT_REFRESH_MS,
  });

export function useBindScreenWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      spaceId: string;
      furnitureId: string;
      kind: WidgetKind;
      title?: string;
      config?: Record<string, unknown>;
    }) => api.post<Widget>('/screens', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['screens'] });
      void qc.invalidateQueries({ queryKey: ['screen-snapshots'] });
    },
  });
}

export function useUpdateScreenWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title?: string | null;
      config?: Record<string, unknown>;
    }) => api.patch<Widget>(`/screens/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['screens'] });
      void qc.invalidateQueries({ queryKey: ['screen-snapshots'] });
    },
  });
}

export function useUnbindScreenWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/screens/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['screens'] });
      void qc.invalidateQueries({ queryKey: ['screen-snapshots'] });
    },
  });
}

/* -------------------------------- files -------------------------------- */

export interface FileFilters {
  folder?: string;
  /** A category id, or `'none'` for everything not filed anywhere. */
  category?: string;
  visibility?: FileVisibility;
  /**
   * A floor, and optionally one room on it. A space on its own is everything
   * filed anywhere on that floor; a space and a room is that room's drawer.
   * Neither is the whole library, which includes both.
   */
  spaceId?: string;
  zoneId?: string;
}

export const useFiles = (filters: FileFilters | string = {}) => {
  const resolved: FileFilters = typeof filters === 'string' ? { folder: filters } : filters;
  const params = new URLSearchParams();
  if (resolved.folder) params.set('folder', resolved.folder);
  if (resolved.category) params.set('category', resolved.category);
  if (resolved.visibility) params.set('visibility', resolved.visibility);
  if (resolved.spaceId) params.set('spaceId', resolved.spaceId);
  if (resolved.spaceId && resolved.zoneId) params.set('zoneId', resolved.zoneId);
  const query = params.toString();

  return useQuery({
    queryKey: ['files', resolved],
    queryFn: () => api.get<List<FileAsset>>(`/files${query ? `?${query}` : ''}`),
  });
};

export interface UploadOptions {
  categoryId?: string;
  visibility?: FileVisibility;
  sharedWith?: string[];
  password?: string;
  /** Filed in this room as well as in the library. Both halves or neither. */
  spaceId?: string;
  zoneId?: string;
}

export function useUploadFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ files, options }: { files: File[]; options?: UploadOptions }) => {
      const form = new FormData();
      for (const file of files) form.append('files', file);
      if (options?.categoryId) form.append('categoryId', options.categoryId);
      if (options?.visibility) form.append('visibility', options.visibility);
      if (options?.password) form.append('password', options.password);
      if (options?.spaceId && options.zoneId) {
        form.append('spaceId', options.spaceId);
        form.append('zoneId', options.zoneId);
      }
      for (const id of options?.sharedWith ?? []) form.append('sharedWith', id);
      return api.upload<List<FileAsset>>('/files', form);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['files'] });
      void qc.invalidateQueries({ queryKey: ['file-categories'] });
      // The room's own count is part of its fittings, and has just changed.
      void qc.invalidateQueries({ queryKey: ['room-tools'] });
    },
  });
}

/** Renames a file, moves it to another category, or changes who it is for. */
export function useUpdateFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      categoryId?: string | null;
      visibility?: FileVisibility;
      sharedWith?: string[];
      /** Sent together, and as `null` to take a file out of its room. */
      spaceId?: string | null;
      zoneId?: string | null;
    }) => api.patch<FileAsset>(`/files/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['files'] });
      void qc.invalidateQueries({ queryKey: ['file-categories'] });
      void qc.invalidateQueries({ queryKey: ['room-tools'] });
    },
  });
}

export function useSetFilePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string | null }) =>
      password === null
        ? api.delete<{ hasPassword: boolean }>(`/files/${id}/password`)
        : api.put<{ hasPassword: boolean }>(`/files/${id}/password`, { password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}

/**
 * Trades a file's password — where one applies — for a short-lived link.
 *
 * Anything narrower than the whole workspace is served from an authenticated
 * route, and `<img>` and `<iframe>` cannot send an auth header, so the URL to
 * open is asked for at the moment of opening rather than kept in the list.
 */
export function useFileAccess() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password?: string }) =>
      api.post<FileAccessGrant>(`/files/${id}/access`, password ? { password } : {}),
  });
}

/* --------------------------------- vault -------------------------------- */

/** Whether a vault has been set up, and how much is in it. */
export const useVaultStatus = () =>
  useQuery({
    queryKey: ['vault-status'],
    queryFn: () => api.get<VaultStatus>('/files/vault/status'),
  });

/**
 * The vault's own listing. Enabled only while the PIN has been typed, so a
 * locked vault does not sit there quietly retrying a 401.
 */
export const useVaultFiles = (open: boolean) =>
  useQuery({
    queryKey: ['vault-files'],
    queryFn: () => api.get<List<FileAsset>>('/files/vault'),
    enabled: open,
    // Nothing about a vault should outlive the vault being shut.
    gcTime: 0,
  });

export function useUnlockVault() {
  return useMutation({
    mutationFn: (pin: string) => api.post<VaultSession>('/files/vault/unlock', { pin }),
  });
}

/**
 * Chooses the PIN, or changes it — which asks for the old one, or for the
 * account password when that has been forgotten. Comes back unlocked either
 * way, so setting up a vault walks straight into it.
 */
export function useSetVaultPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { pin: string; currentPin?: string; password?: string }) =>
      api.post<VaultSession>('/files/vault/pin', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault-status'] }),
  });
}

/** Moves a vaulted file to another shelf without taking it out. */
export function useSetVaultShelf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, category }: { id: string; category: VaultCategory }) =>
      api.patch<FileAsset>(`/files/${id}/vault`, { category }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault-files'] }),
  });
}

/* --------------------------- typed vault entries -------------------------- */

/** Logins, cards and notes typed into the vault rather than uploaded. */
export const useVaultEntries = (open: boolean) =>
  useQuery({
    queryKey: ['vault-entries'],
    queryFn: () => api.get<List<VaultEntryView>>('/files/vault/entries'),
    enabled: open,
    gcTime: 0,
  });

export function useSaveVaultEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: VaultEntryInput & { id?: string }) =>
      id
        ? api.patch<VaultEntryView>(`/files/vault/entries/${id}`, body)
        : api.post<VaultEntryView>('/files/vault/entries', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vault-entries'] });
      void qc.invalidateQueries({ queryKey: ['vault-status'] });
    },
  });
}

export function useDeleteVaultEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/files/vault/entries/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vault-entries'] });
      void qc.invalidateQueries({ queryKey: ['vault-status'] });
    },
  });
}

/**
 * Asks for the sealed half of one entry.
 *
 * A mutation rather than a query on purpose: revealing a password is
 * something somebody did, and it must not be a thing the cache repeats on a
 * refetch or hands to the next page that asks.
 */
export function useRevealVaultEntry() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<VaultEntrySecrets>(`/files/vault/entries/${id}/reveal`),
  });
}

/**
 * Files what is already in the vault by what each file is.
 *
 * Only touches the `Other` shelf, so a tidy-up never overwrites filing that
 * was done by hand.
 */
export function useSortVault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<VaultSortResult>('/files/vault/sort'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault-files'] }),
  });
}

/**
 * Switches the vault off. The server refuses while it still holds anything,
 * so this is only ever offered on an empty one.
 */
export function useForgetVaultPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<VaultStatus>('/files/vault/pin'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault-status'] }),
  });
}

/** Moves a file into the vault, or back out of it. */
export function useMoveToVault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      into,
      category,
    }: {
      id: string;
      into: boolean;
      category?: VaultCategory;
    }) =>
      into
        ? api.post<FileAsset>(`/files/${id}/vault`, category ? { category } : undefined)
        : api.delete<FileAsset>(`/files/${id}/vault`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['files'] });
      void qc.invalidateQueries({ queryKey: ['vault-files'] });
      void qc.invalidateQueries({ queryKey: ['vault-status'] });
      void qc.invalidateQueries({ queryKey: ['file-categories'] });
    },
  });
}

/* ---------------------------- file categories --------------------------- */

export const useFileCategories = () =>
  useQuery({
    queryKey: ['file-categories'],
    queryFn: () => api.get<List<FileCategory>>('/files/categories'),
    staleTime: 60_000,
  });

export function useCreateFileCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; color?: string; description?: string }) =>
      api.post<FileCategory>('/files/categories', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['file-categories'] }),
  });
}

export function useUpdateFileCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; color?: string }) =>
      api.patch<FileCategory>(`/files/categories/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['file-categories'] }),
  });
}

export function useDeleteFileCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/files/categories/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['file-categories'] });
      void qc.invalidateQueries({ queryKey: ['files'] });
    },
  });
}

export function useUploadAvatar() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('files', file);
      form.append('purpose', 'avatar');
      return api.upload<List<FileAsset>>('/files', form);
    },
  });
}

export function useDeleteFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/files/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['files'] });
      void qc.invalidateQueries({ queryKey: ['room-tools'] });
    },
  });
}

/* ------------------------------- documents ------------------------------ */

/**
 * Document metadata. The paragraphs arrive over the socket, the same way a
 * sheet's cells do — a query that refetched the body would fight with the
 * live edits already applied to it.
 */
export const useDoc = (docId?: string) =>
  useQuery({
    queryKey: ['doc', docId],
    queryFn: () => api.get<Doc>(`/docs/${docId}`),
    enabled: Boolean(docId),
  });

/**
 * Opens a document: blank, or the live copy of an uploaded file. Passing the
 * same `fileId` twice joins the document that already exists, so this doubles
 * as "open" and there is no create-then-open dance.
 */
export function useOpenDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string; fileId?: string }) => api.post<Doc>('/docs', body),
    onSuccess: (doc) => {
      void qc.invalidateQueries({ queryKey: ['docs'] });
      qc.setQueryData(['doc', doc.id], doc);
    },
  });
}

/**
 * Opens a deck: blank, or the live copy of a `.pptx` in the library.
 * Idempotent per file, for the same reason a document is.
 */
export function useOpenSlides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string; fileId?: string }) =>
      api.post<SlideDeck>('/slides', body),
    onSuccess: (deck) => {
      void qc.invalidateQueries({ queryKey: ['slides'] });
      qc.setQueryData(['slides', deck.id], deck);
    },
  });
}

/**
 * Makes a document, workbook or deck from nothing.
 *
 * Answers with the file row *and* the live document behind it, so the caller
 * can drop straight into the editor without a second round trip asking what
 * it just made.
 */
export function useCreateLibraryFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      kind: LibraryFileKind;
      name: string;
      visibility: FileVisibility;
      sharedWith?: string[];
      categoryId?: string;
      spaceId?: string;
      zoneId?: string;
    }) => api.post<NewLibraryFile>('/files/new', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['files'] });
      void qc.invalidateQueries({ queryKey: ['file-categories'] });
      void qc.invalidateQueries({ queryKey: ['room-tools'] });
    },
  });
}

/**
 * Drafts a library file with the assistant.
 *
 * Separate from {@link useCreateLibraryFile} despite the identical response,
 * because the two behave differently where it matters to a caller: this one
 * waits on a model and takes seconds rather than milliseconds, so the UI
 * around it has to say so.
 */
export function useGenerateLibraryFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      kind: LibraryFileKind;
      brief: string;
      name?: string;
      projectId?: string;
      length: GeneratedFileLength;
      visibility: FileVisibility;
      sharedWith?: string[];
      categoryId?: string;
      spaceId?: string;
      zoneId?: string;
    }) => api.post<NewLibraryFile>('/files/generate', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['files'] });
      void qc.invalidateQueries({ queryKey: ['file-categories'] });
      void qc.invalidateQueries({ queryKey: ['room-tools'] });
    },
  });
}

/** The notes page for a meeting, created on first use and shared by the room. */
export function useMeetingDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) => api.post<Doc>(`/docs/meeting/${meetingId}`, {}),
    onSuccess: (doc) => qc.setQueryData(['doc', doc.id], doc),
  });
}

export function useRenameDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.patch<Doc>(`/docs/${id}`, { title }),
    onSuccess: (doc) => {
      void qc.invalidateQueries({ queryKey: ['docs'] });
      qc.setQueryData(['doc', doc.id], doc);
    },
  });
}

export function useDeleteDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/docs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['docs'] }),
  });
}

/* ----------------------------- file comments ---------------------------- */

export const useFileComments = (fileId?: string) =>
  useQuery({
    queryKey: ['fileComments', fileId],
    queryFn: () => api.get<List<FileComment>>(`/files/${fileId}/comments`),
    enabled: Boolean(fileId),
  });

export function useAddFileComment(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string; parentId?: string }) =>
      api.post<FileComment>(`/files/${fileId}/comments`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fileComments', fileId] }),
  });
}

export function useEditFileComment(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      api.patch<FileComment>(`/files/${fileId}/comments/${id}`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fileComments', fileId] }),
  });
}

export function useDeleteFileComment(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/files/${fileId}/comments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fileComments', fileId] }),
  });
}

/* ----------------------------- spreadsheets ---------------------------- */

/**
 * Workbook metadata. The cells arrive over the socket, the same way a
 * whiteboard's strokes do — a query that refetched the grid would fight with
 * the live edits already applied to it.
 */
export const useSheet = (sheetId?: string) =>
  useQuery({
    queryKey: ['sheet', sheetId],
    queryFn: () => api.get<Sheet>(`/sheets/${sheetId}`),
    enabled: Boolean(sheetId),
  });

/** Workbooks made from a given upload — used to label the file's row. */
export const useSheetsForFile = (fileId?: string) =>
  useQuery({
    queryKey: ['sheets', { fileId }],
    queryFn: () => api.get<List<SheetSummary>>(`/sheets?fileId=${fileId}`),
    enabled: Boolean(fileId),
  });

/**
 * Opens a spreadsheet: blank, or the live copy of an uploaded file. Passing
 * the same `fileId` twice returns the workbook that already exists, so this
 * doubles as "open" and there is no create-then-open dance.
 */
export function useOpenSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; fileId?: string }) => api.post<Sheet>('/sheets', body),
    onSuccess: (sheet) => {
      void qc.invalidateQueries({ queryKey: ['sheets'] });
      qc.setQueryData(['sheet', sheet.id], sheet);
    },
  });
}

export function useRenameSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<Sheet>(`/sheets/${id}`, { name }),
    onSuccess: (sheet) => {
      void qc.invalidateQueries({ queryKey: ['sheets'] });
      qc.setQueryData(['sheet', sheet.id], sheet);
    },
  });
}

export function useDeleteSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/sheets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sheets'] }),
  });
}

export function useAddSheetTab() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post<Sheet>(`/sheets/${id}/tabs`, { name }),
    onSuccess: (sheet) => qc.setQueryData(['sheet', sheet.id], sheet),
  });
}

export function useRenameSheetTab() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tabId, name }: { id: string; tabId: string; name: string }) =>
      api.patch<Sheet>(`/sheets/${id}/tabs/${tabId}`, { name }),
    onSuccess: (sheet) => qc.setQueryData(['sheet', sheet.id], sheet),
  });
}

/**
 * Grows a tab's grid.
 *
 * Separate from renaming despite the same route, because the two are asked
 * for in completely different places — a rename comes from a dialog, more
 * rows from the toolbar — and a caller should not have to name a tab it is
 * not renaming.
 */
export function useGrowSheetTab() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      tabId,
      rows,
      cols,
    }: {
      id: string;
      tabId: string;
      rows?: number;
      cols?: number;
    }) => api.patch<Sheet>(`/sheets/${id}/tabs/${tabId}`, { rows, cols }),
    onSuccess: (sheet) => qc.setQueryData(['sheet', sheet.id], sheet),
  });
}

export function useDeleteSheetTab() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tabId }: { id: string; tabId: string }) =>
      api.delete<Sheet>(`/sheets/${id}/tabs/${tabId}`),
    onSuccess: (sheet) => {
      if (sheet) qc.setQueryData(['sheet', sheet.id], sheet);
    },
  });
}

/* ----------------------------- whiteboards ----------------------------- */

export interface WhiteboardFilter {
  scope?: WhiteboardScope;
  spaceId?: string;
  meetingId?: string;
  /** Only boards the signed-in person opened. */
  mine?: boolean;
}

function whiteboardParams(filter: WhiteboardFilter = {}): string {
  const params = new URLSearchParams();
  if (filter.scope) params.set('scope', filter.scope);
  if (filter.spaceId) params.set('spaceId', filter.spaceId);
  if (filter.meetingId) params.set('meetingId', filter.meetingId);
  if (filter.mine) params.set('mine', '1');
  const query = params.toString();
  return query ? `?${query}` : '';
}

export const useWhiteboards = (filter: WhiteboardFilter = {}) =>
  useQuery({
    queryKey: ['whiteboards', filter],
    queryFn: () => api.get<List<WhiteboardSummary>>(`/whiteboards${whiteboardParams(filter)}`),
  });

/** Board metadata — name, scope, who opened it. The strokes arrive over the socket. */
export const useWhiteboard = (boardId?: string) =>
  useQuery({
    queryKey: ['whiteboard', boardId],
    queryFn: () => api.get<Whiteboard>(`/whiteboards/${boardId}`),
    enabled: Boolean(boardId),
  });

export function useCreateWhiteboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; spaceId?: string; meetingId?: string }) =>
      api.post<Whiteboard>('/whiteboards', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whiteboards'] }),
  });
}

export function useRenameWhiteboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<Whiteboard>(`/whiteboards/${id}`, { name }),
    onSuccess: (board) => {
      void qc.invalidateQueries({ queryKey: ['whiteboards'] });
      qc.setQueryData(['whiteboard', board.id], board);
    },
  });
}

export function useDeleteWhiteboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/whiteboards/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whiteboards'] }),
  });
}

/** Empties a board for everyone. Only its creator (or an admin) may. */
export function useClearWhiteboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/whiteboards/${id}/clear`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whiteboards'] }),
  });
}

/**
 * The board for a meeting, created on first use. Idempotent, so the meeting
 * room can offer one button rather than "create" and "open".
 */
export function useMeetingWhiteboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      api.post<Whiteboard>(`/whiteboards/meeting/${meetingId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whiteboards'] }),
  });
}

/* ------------------------------ meetings ------------------------------- */

/**
 * `refetchInterval` is for the office floor: a session somebody else starts in
 * the room you are standing in arrives as a row in this list and nothing else,
 * so whoever is standing in a room polls it. Everywhere else — a calendar, a
 * meetings page — keeps the default and does not.
 */
export const useMeetings = (
  range?: { from?: string; to?: string },
  options?: { refetchInterval?: number },
) =>
  useQuery({
    queryKey: ['meetings', range],
    queryFn: () => {
      const params = new URLSearchParams();
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
      return api.get<List<Meeting & { liveParticipants: number }>>(
        `/meetings${params.size ? `?${params}` : ''}`,
      );
    },
    refetchInterval: options?.refetchInterval,
  });

export const useMeeting = (id?: string) =>
  useQuery({
    queryKey: ['meeting', id],
    queryFn: () => api.get<Meeting & { liveParticipants: string[] }>(`/meetings/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

export function useCreateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Meeting>('/meetings', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meetings'] }),
  });
}

export function useUpdateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch<Meeting>(`/meetings/${id}`, body),
    onSuccess: (meeting) => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
      qc.invalidateQueries({ queryKey: ['meeting', meeting.id] });
    },
  });
}

export function useDeleteMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/meetings/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meetings'] }),
  });
}

/* ------------------------- meeting attachments -------------------------- */

/**
 * The papers that go with a meeting: an agenda, a deck, last month's numbers.
 *
 * Same arrangement as a task's files — the meeting stores ids, and the files
 * live in a folder named after it, so the existing listing endpoint finds them
 * with no server change and both clients see the same set.
 */
const meetingFolder = (meetingId: string) => `/meetings/${meetingId}`;

export const useMeetingFiles = (meetingId?: string) =>
  useQuery({
    queryKey: ['meeting', meetingId, 'files'],
    queryFn: () =>
      api.get<List<FileAsset>>(
        `/files?folder=${encodeURIComponent(meetingFolder(meetingId!))}`,
      ),
    enabled: Boolean(meetingId),
  });

/**
 * Reads the meeting back before patching rather than trusting a cached copy:
 * this runs from a list page, where the single meeting may never have been
 * fetched, and two people attaching at once must not drop each other's file.
 */
async function withAttachments(meetingId: string, change: (ids: string[]) => string[]) {
  const meeting = await api.get<Meeting>(`/meetings/${meetingId}`);
  await api.patch(`/meetings/${meetingId}`, {
    attachmentIds: change(meeting.attachmentIds ?? []),
  });
}

export function useUploadMeetingFiles(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]) => {
      const form = new FormData();
      for (const file of files) form.append('files', file);
      form.append('folder', meetingFolder(meetingId));
      const result = await api.upload<List<FileAsset>>('/files', form);
      await withAttachments(meetingId, (ids) => [...ids, ...result.items.map((f) => f.id)]);
      return result.items;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meeting', meetingId, 'files'] });
      void qc.invalidateQueries({ queryKey: ['meeting', meetingId] });
      void qc.invalidateQueries({ queryKey: ['meetings'] });
      void qc.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (err) =>
      toast.error("Couldn't attach that file", err instanceof Error ? err.message : undefined),
  });
}

/** Takes a file off a meeting and deletes it — a meeting folder is not a library. */
export function useRemoveMeetingFile(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: string) => {
      await api.delete(`/files/${fileId}`);
      await withAttachments(meetingId, (ids) => ids.filter((id) => id !== fileId));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meeting', meetingId, 'files'] });
      void qc.invalidateQueries({ queryKey: ['meeting', meetingId] });
      void qc.invalidateQueries({ queryKey: ['meetings'] });
      void qc.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (err) =>
      toast.error("Couldn't remove that file", err instanceof Error ? err.message : undefined),
  });
}

export function useMeetingGuestInvites(meetingId?: string, enabled = true) {
  return useQuery({
    queryKey: ['meeting-guest-invites', meetingId],
    queryFn: () => api.get<List<MeetingGuestInvite>>(`/meetings/${meetingId}/guest-invites`),
    enabled: Boolean(meetingId && enabled),
    refetchInterval: meetingId && enabled ? 5_000 : false,
  });
}

export function useCreateMeetingGuestInvite(meetingId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ invite: MeetingGuestInvite; path: string }>(
        `/meetings/${meetingId}/guest-invites`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meeting-guest-invites', meetingId] }),
  });
}

export function useRevokeMeetingGuestInvite(meetingId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) =>
      api.delete<void>(`/meetings/${meetingId}/guest-invites/${inviteId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meeting-guest-invites', meetingId] }),
  });
}

/* -------------------------------- games -------------------------------- */

export const useGames = (
  params: { status?: string; mine?: boolean; spaceId?: string } = {},
) => {
  const search = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== false && v !== '')
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  return useQuery({
    queryKey: ['games', params],
    queryFn: () => api.get<List<GameSummary>>(`/games${search ? `?${search}` : ''}`),
  });
};

export const useGame = (id?: string) =>
  useQuery({
    queryKey: ['game', id],
    queryFn: () => api.get<Game>(`/games/${id}`),
    enabled: Boolean(id),
  });

export const useLeaderboard = (spaceId?: string, kind?: GameKind) =>
  useQuery({
    queryKey: ['games', 'leaderboard', spaceId ?? 'workspace', kind ?? 'all'],
    queryFn: () => {
      const params = new URLSearchParams();
      if (spaceId) params.set('spaceId', spaceId);
      if (kind) params.set('kind', kind);
      return api.get<List<GameStanding>>(
        `/games/leaderboard${params.size ? `?${params}` : ''}`,
      );
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

export const useSoloLeaderboard = (spaceId: string | undefined, kind: SoloGameKind) =>
  useQuery({
    queryKey: ['games', 'solo-leaderboard', spaceId ?? 'workspace', kind],
    queryFn: () => {
      const params = new URLSearchParams({ kind });
      if (spaceId) params.set('spaceId', spaceId);
      return api.get<List<SoloGameStanding>>(`/games/solo-leaderboard?${params}`);
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

export const useSoloImpact = (spaceId?: string) =>
  useQuery({
    queryKey: ['games', 'solo-impact', spaceId ?? 'workspace'],
    queryFn: () =>
      api.get<SoloGameImpact>(
        `/games/solo-impact${spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : ''}`,
      ),
    staleTime: 30_000,
  });

export function useSubmitSoloResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      sessionId: string;
      kind: SoloGameKind;
      score: number;
      durationSeconds: number;
      moves?: number;
      spaceId?: string;
      stressBefore?: number;
      stressAfter?: number;
    }) => api.post<SoloGameResult>('/games/solo-results', body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['games', 'solo-leaderboard'] });
      void qc.invalidateQueries({ queryKey: ['games', 'solo-impact'] });
      return result;
    },
  });
}

export function useCreateGame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      kind: GameKind;
      name?: string;
      opponentId?: string;
      spaceId?: string;
      zoneId?: string;
    }) => api.post<Game>('/games', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['games'] }),
  });
}

export function useJoinGame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Game>(`/games/${id}/join`),
    onSuccess: (game) => {
      qc.setQueryData(['game', game.id], game);
      void qc.invalidateQueries({ queryKey: ['games'] });
    },
  });
}

export function usePlayMove(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (move: GameMove) => api.post<Game>(`/games/${gameId}/moves`, move),
    // The socket broadcast arrives too, but writing the response straight in
    // keeps the mover's own board from flickering back a move.
    onSuccess: (game) => qc.setQueryData(['game', gameId], game),
  });
}

export function useResignGame(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Game>(`/games/${gameId}/resign`),
    onSuccess: (game) => {
      qc.setQueryData(['game', gameId], game);
      void qc.invalidateQueries({ queryKey: ['games'] });
    },
  });
}

export function useRematch(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Game>(`/games/${gameId}/rematch`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['games'] }),
  });
}

export function useDeleteGame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/games/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['games'] }),
  });
}

/* --------------------------- notifications ----------------------------- */

export const useNotifications = () =>
  useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<List<Notification> & { unreadCount: number }>('/notifications'),
    refetchInterval: 60_000,
  });

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) => api.post('/notifications/read', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export const useActivity = () =>
  useQuery({
    queryKey: ['activity'],
    queryFn: () => api.get<List<Activity>>('/notifications/activity'),
  });

/* ------------------------------- search -------------------------------- */

export interface SearchResults {
  tasks: Task[];
  projects: Project[];
  messages: (Message & { channelName?: string })[];
  files: FileAsset[];
  channels: { id: string; name: string }[];
  people: PublicUser[];
}

export const useSearch = (q: string) =>
  useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get<SearchResults>(`/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length > 1,
    staleTime: 15_000,
  });

/* --------------------------- team management ---------------------------- */

export const useAnnouncements = (enabled = true) =>
  useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.get<List<Announcement>>('/announcements'),
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled,
  });

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; body?: string; requireAck: boolean }) =>
      api.post<Announcement>('/announcements', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function useAckAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/announcements/${id}/ack`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function useCloseAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch(`/announcements/${id}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/announcements/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

/* ----------------------------- hierarchy -------------------------------- */

export const useOrgChart = () =>
  useQuery({
    queryKey: ['org-chart'],
    queryFn: () => api.get<OrgChart>('/hierarchy/chart'),
    staleTime: 60_000,
  });

export const useDepartments = () =>
  useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<List<Department>>('/hierarchy/departments'),
    staleTime: 60_000,
  });

export interface DepartmentInput {
  name?: string;
  description?: string;
  parentId?: string | null;
  leadId?: string | null;
}

/**
 * Every hierarchy mutation touches both trees — moving a department changes
 * the chart, and naming a lead changes the department list — so they all
 * invalidate the pair rather than each guessing which half it affected.
 */
function invalidateHierarchy(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['org-chart'] });
  void qc.invalidateQueries({ queryKey: ['departments'] });
  void qc.invalidateQueries({ queryKey: ['members'] });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DepartmentInput & { name: string }) =>
      api.post<Department>('/hierarchy/departments', body),
    onSuccess: () => invalidateHierarchy(qc),
  });
}

export function useUpdateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: DepartmentInput & { id: string }) =>
      api.patch<Department>(`/hierarchy/departments/${id}`, body),
    onSuccess: () => invalidateHierarchy(qc),
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/hierarchy/departments/${id}`),
    onSuccess: () => invalidateHierarchy(qc),
  });
}

/** Absent fields are left alone; null clears one. */
export function useSetMemberPlacement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      ...body
    }: {
      userId: string;
      departmentId?: string | null;
      managerId?: string | null;
    }) => api.patch<OrgChart>(`/hierarchy/members/${userId}`, body),
    onSuccess: () => invalidateHierarchy(qc),
  });
}

/* ------------------------------ policies -------------------------------- */

export const usePolicies = () =>
  useQuery({
    queryKey: ['policies'],
    queryFn: () => api.get<List<Policy>>('/policies'),
    staleTime: 30_000,
  });

export interface PolicyInput {
  kind?: PolicyKind;
  title?: string;
  summary?: string;
  body?: string;
  requireAck?: boolean;
  order?: number;
}

export function useCreatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PolicyInput & { title: string; body: string }) =>
      api.post<Policy>('/policies', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export function useUpdatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: PolicyInput & { id: string }) =>
      api.patch<Policy>(`/policies/${id}`, body),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['policies'] });
      void qc.invalidateQueries({ queryKey: ['policy-acks', variables.id] });
    },
  });
}

export function usePublishPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Policy>(`/policies/${id}/publish`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export function useAckPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/policies/${id}/ack`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['policies'] });
      void qc.invalidateQueries({ queryKey: ['policy-acks', id] });
    },
  });
}

export function useDeletePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/policies/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

/** The signature report: who has signed, and who has not. */
export const usePolicyAcks = (id: string | null) =>
  useQuery({
    queryKey: ['policy-acks', id],
    queryFn: () =>
      api.get<{ items: PolicyAck[]; outstanding: PublicUser[]; version: number }>(
        `/policies/${id}/acks`,
      ),
    enabled: Boolean(id),
  });

export const useAuditLog = (filters: {
  targetType?: ActivityTargetType;
  actorId?: string;
  cursor?: string;
}) =>
  useQuery({
    queryKey: ['audit', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.targetType) params.set('targetType', filters.targetType);
      if (filters.actorId) params.set('actorId', filters.actorId);
      if (filters.cursor) params.set('cursor', filters.cursor);
      const qs = params.toString();
      return api.get<List<Activity>>(`/audit${qs ? `?${qs}` : ''}`);
    },
    staleTime: 15_000,
  });

export const useTeamReport = (days: number, enabled = true) =>
  useQuery({
    queryKey: ['team-report', days],
    queryFn: () => api.get<TeamReport>(`/reports/summary?days=${days}`),
    staleTime: 60_000,
    enabled,
  });

export const useTeamNow = (enabled = true) =>
  useQuery({
    queryKey: ['team-now'],
    queryFn: () => api.get<List<TeamNowEntry>>('/reports/team-now'),
    refetchInterval: 15_000,
    enabled,
  });

export const useTeamWorkload = (enabled = true) =>
  useQuery({
    queryKey: ['team-workload'],
    queryFn: () => api.get<List<TeamWorkloadEntry>>('/reports/workload'),
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled,
  });

export const useCheckInConfig = () =>
  useQuery({
    queryKey: ['checkin-config'],
    queryFn: () => api.get<CheckInConfig>('/checkins/config'),
    staleTime: 60_000,
  });

export function useSaveCheckInConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      enabled: boolean;
      question: string;
      remindAt?: string;
      timezone?: string;
    }) => api.put<CheckInConfig>('/checkins/config', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checkin-config'] }),
  });
}

export const useMyCheckIns = () =>
  useQuery({
    queryKey: ['checkins', 'mine'],
    queryFn: () => api.get<List<CheckInEntry>>('/checkins/mine'),
    staleTime: 30_000,
  });

export const useTeamCheckIns = (day: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ['checkins', day ?? 'today'],
    queryFn: () =>
      api.get<{ day: string; items: CheckInEntry[] }>(`/checkins${day ? `?day=${day}` : ''}`),
    staleTime: 30_000,
    enabled,
  });

export function useSubmitCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string }) => api.post<CheckInEntry>('/checkins', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checkins'] });
      qc.invalidateQueries({ queryKey: ['checkins', 'mine'] });
    },
  });
}

export const useMyLeave = () =>
  useQuery({
    queryKey: ['leave', 'mine'],
    queryFn: () => api.get<List<LeaveRequest>>('/leave/mine'),
    staleTime: 30_000,
  });

export const useLeaveRequests = (status: LeaveStatus | undefined, enabled = true) =>
  useQuery({
    queryKey: ['leave', status ?? 'all'],
    queryFn: () => api.get<List<LeaveRequest>>(`/leave${status ? `?status=${status}` : ''}`),
    staleTime: 15_000,
    enabled,
  });

export const useLeaveCalendar = (from: string, to: string) =>
  useQuery({
    queryKey: ['leave-calendar', from, to],
    queryFn: () => api.get<List<LeaveRequest>>(`/leave/calendar?from=${from}&to=${to}`),
    staleTime: 60_000,
  });

/** Birthdays and work anniversaries in the next `days`, soonest first. */
export const useCelebrations = (days = 30) =>
  useQuery({
    queryKey: ['celebrations', days],
    queryFn: () => api.get<List<Celebration>>(`/celebrations?days=${days}`),
    staleTime: 5 * 60_000,
  });

/* ------------------------------ coffee chat ----------------------------- */

/**
 * Today's coffee pairing. Answered even when there is none, because "the
 * workspace does not do this", "you opted out" and "not drawn yet" are three
 * different things to say to whoever is looking at the card.
 */
export const useCoffeeToday = () =>
  useQuery({
    queryKey: ['coffee-today'],
    queryFn: () => api.get<CoffeeToday>('/coffee/today'),
    staleTime: 60_000,
  });

export const useCoffeeHistory = (weeks = 4, enabled = true) =>
  useQuery({
    queryKey: ['coffee-history', weeks],
    queryFn: () => api.get<List<CoffeePairing>>(`/coffee/history?weeks=${weeks}`),
    staleTime: 5 * 60_000,
    enabled,
  });

/** "We met" and "Skip" — the same shape, so they share a hook. */
export function useSettleCoffee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome: 'met' | 'skip' }) =>
      api.post<CoffeePairing>(`/coffee/${id}/${outcome}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['coffee-today'] });
      void qc.invalidateQueries({ queryKey: ['coffee-history'] });
      // A coffee that happened is points, which the engagement card shows.
      void qc.invalidateQueries({ queryKey: ['engagement'] });
    },
  });
}

/** The administration button: draws this workspace's pairings immediately. */
export function useRunCoffeeDraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ drawn: number }>('/coffee/run-now'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['coffee-today'] });
      void qc.invalidateQueries({ queryKey: ['coffee-history'] });
    },
  });
}

/** The day someone started with the company; `null` counts from when they joined. */
export function useSetMemberStartDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, startedAt }: { userId: string; startedAt: string | null }) =>
      api.patch<Membership>(`/orgs/members/${userId}/start-date`, { startedAt }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['members'] });
      void qc.invalidateQueries({ queryKey: ['celebrations'] });
    },
  });
}

export function useRequestLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      kind: LeaveKind;
      startDate: string;
      endDate: string;
      note?: string;
    }) => api.post<LeaveRequest>('/leave', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave'] }),
  });
}

export function useReviewLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      status: 'approved' | 'rejected';
      note?: string;
    }) => api.post<LeaveRequest>(`/leave/${id}/review`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave'] });
      qc.invalidateQueries({ queryKey: ['leave-calendar'] });
    },
  });
}

export function useWithdrawLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/leave/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave'] }),
  });
}

export const useWorkSchedules = (enabled = true) =>
  useQuery({
    queryKey: ['schedules'],
    queryFn: () => api.get<List<WorkSchedule>>('/schedules'),
    staleTime: 60_000,
    enabled,
  });

export function useSaveWorkSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      ...body
    }: {
      userId: string;
      days: number[];
      startTime: string;
      endTime: string;
      timezone: string;
      alertLateMinutes: number;
    }) => api.put<WorkSchedule>(`/schedules/${userId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}

export function useDeleteWorkSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/schedules/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}

/* --------------------------- physical office ---------------------------- */

export const useOfficeSites = (enabled = true) =>
  useQuery({
    queryKey: ['office-sites'],
    queryFn: () => api.get<List<OfficeSite>>('/office/sites'),
    staleTime: 60_000,
    enabled,
  });

interface OfficeSiteInput {
  name: string;
  address?: string;
  lat: number;
  lng: number;
  radiusMeters: number;
}

export function useCreateOfficeSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OfficeSiteInput) => api.post<OfficeSite>('/office/sites', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['office-sites'] }),
  });
}

export function useUpdateOfficeSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: OfficeSiteInput & { id: string }) =>
      api.put<OfficeSite>(`/office/sites/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['office-sites'] }),
  });
}

export function useDeleteOfficeSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/office/sites/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['office-sites'] });
      qc.invalidateQueries({ queryKey: ['office-assignments'] });
    },
  });
}

export const useOfficeAssignments = (enabled = true) =>
  useQuery({
    queryKey: ['office-assignments'],
    queryFn: () => api.get<List<OfficeAssignment>>('/office/assignments'),
    staleTime: 60_000,
    enabled,
  });

/** Replaces a member's whole set of home offices; an empty list is "any office". */
export function useAssignOfficeSites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, siteIds }: { userId: string; siteIds: string[] }) =>
      api.put<OfficeAssignment>(`/office/assignments/${userId}`, { siteIds }),
    /* Each request carries the member's whole set, so the next toggle has to
       read the previous one — a cache that only catches up on the round-trip
       would let a quick second click undo the first. */
    onMutate: ({ userId, siteIds }) =>
      qc.setQueryData<List<OfficeAssignment>>(['office-assignments'], (current) => {
        const items = current?.items ?? [];
        return items.some((entry) => entry.userId === userId)
          ? {
              ...current,
              items: items.map((e) => (e.userId === userId ? { ...e, siteIds } : e)),
            }
          : { ...current, items: [...items, { userId, siteIds }] };
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['office-assignments'] }),
  });
}

export const useOfficeToday = (enabled = true) =>
  useQuery({
    queryKey: ['office-today'],
    queryFn: () => api.get<{ day: string; items: OfficeTodayEntry[] }>('/office/today'),
    refetchInterval: 30_000,
    enabled,
  });

/**
 * The caller's own offices — the places they are assigned to, or every saved
 * place when they are assigned none. An empty list means the workspace has
 * saved no place, which is what hides the check-in card entirely.
 */
export const useMyOfficeSites = () =>
  useQuery({
    queryKey: ['office-site', 'mine'],
    queryFn: () => api.get<List<OfficeSite>>('/office/sites/mine'),
    staleTime: 60_000,
  });

const invalidateOfficePresence = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['office-today'] });
  qc.invalidateQueries({ queryKey: ['members'] });
};

export function useOfficeCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { lat: number; lng: number; accuracy?: number }) =>
      api.post<{ siteName: string; distanceMeters: number }>('/office/check-in', {
        ...body,
        method: 'manual',
      }),
    onSuccess: () => invalidateOfficePresence(qc),
  });
}

export function useOfficeCheckOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/office/check-out'),
    onSuccess: () => invalidateOfficePresence(qc),
  });
}

export const useOnboardingTemplates = (enabled = true) =>
  useQuery({
    queryKey: ['onboarding-templates'],
    queryFn: () => api.get<List<OnboardingTemplate>>('/onboarding/templates'),
    staleTime: 60_000,
    enabled,
  });

export function useSaveOnboardingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id?: string;
      name: string;
      isDefault: boolean;
      steps: { title: string; description?: string; link?: string }[];
    }) =>
      id
        ? api.patch<OnboardingTemplate>(`/onboarding/templates/${id}`, body)
        : api.post<OnboardingTemplate>('/onboarding/templates', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-templates'] }),
  });
}

export function useDeleteOnboardingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/onboarding/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-templates'] }),
  });
}

export const useMyOnboarding = () =>
  useQuery({
    queryKey: ['onboarding', 'mine'],
    queryFn: () => api.get<OnboardingProgress | null>('/onboarding/mine'),
    staleTime: 30_000,
  });

export function useToggleOnboardingStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, done }: { stepId: string; done: boolean }) =>
      api.post<OnboardingProgress>(`/onboarding/mine/steps/${stepId}`, { done }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding', 'mine'] });
      qc.invalidateQueries({ queryKey: ['onboarding-progress'] });
    },
  });
}

export const useOnboardingProgress = (enabled = true) =>
  useQuery({
    queryKey: ['onboarding-progress'],
    queryFn: () => api.get<List<OnboardingProgress>>('/onboarding/progress'),
    staleTime: 30_000,
    enabled,
  });

export const useSurveys = () =>
  useQuery({
    queryKey: ['surveys'],
    queryFn: () => api.get<List<PulseSurvey>>('/surveys'),
    staleTime: 30_000,
  });

export function useCreateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { question: string; anonymous: boolean; closesInDays?: number }) =>
      api.post<PulseSurvey>('/surveys', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys'] }),
  });
}

export function useRespondSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; score: number; comment?: string }) =>
      api.post<PulseSurvey>(`/surveys/${id}/respond`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys'] }),
  });
}

export function useCloseSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch<PulseSurvey>(`/surveys/${id}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys'] }),
  });
}

export function useDeleteSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/surveys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys'] }),
  });
}

export const useSessions = () =>
  useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<List<SessionInfo>>('/sessions'),
    staleTime: 15_000,
  });

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

export function useSignOutEverywhere() {
  return useMutation({
    mutationFn: () => api.delete('/sessions'),
  });
}

export function useSetMemberExpiry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, expiresAt }: { userId: string; expiresAt: string | null }) =>
      api.patch<Membership>(`/orgs/members/${userId}/expiry`, { expiresAt }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });
}

export function useForceSignOutMember() {
  return useMutation({
    mutationFn: (userId: string) => api.post(`/orgs/members/${userId}/sign-out`),
  });
}

/* ------------------------------- finance -------------------------------- */

/** The shape both budget writers send; `scope` carries its own target id. */
interface BudgetInput {
  name: string;
  scope: FinanceScope;
  departmentId?: string | null;
  projectId?: string | null;
  amount: number;
  startDate: string;
  endDate: string;
  note?: string;
}

interface ExpenseInput {
  description: string;
  vendor?: string;
  /** Workspace currency, minor units — the figure the books add up. */
  amount: number;
  /** What the receipt said, when it was in another currency. Null clears. */
  originalAmount?: number | null;
  originalCurrency?: CurrencyCode | null;
  category: ExpenseCategory;
  date: string;
  scope: FinanceScope;
  departmentId?: string | null;
  projectId?: string | null;
  receiptId?: string | null;
}

export interface ExpenseFilters {
  status?: ExpenseStatus;
  category?: ExpenseCategory;
  from?: string;
  to?: string;
}

/**
 * Every finance write invalidates the summary as well as the list it changed:
 * approving one expense moves the totals, the category split and the standing
 * of whichever budget it lands in, and none of those can be patched in place.
 */
const invalidateFinance = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: ['budgets'] });
  void qc.invalidateQueries({ queryKey: ['expenses'] });
  void qc.invalidateQueries({ queryKey: ['finance-summary'] });
};

export const useBudgets = (enabled = true) =>
  useQuery({
    queryKey: ['budgets'],
    queryFn: () => api.get<List<Budget>>('/finance/budgets'),
    staleTime: 60_000,
    enabled,
  });

export function useCreateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BudgetInput) => api.post<Budget>('/finance/budgets', body),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useUpdateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<BudgetInput> & { id: string }) =>
      api.patch<Budget>(`/finance/budgets/${id}`, body),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useDeleteBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/finance/budgets/${id}`),
    onSuccess: () => invalidateFinance(qc),
  });
}

export const useExpenses = (filters: ExpenseFilters = {}, enabled = true) =>
  useQuery({
    queryKey: ['expenses', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
      const qs = params.toString();
      return api.get<List<Expense>>(`/finance/expenses${qs ? `?${qs}` : ''}`);
    },
    staleTime: 30_000,
    enabled,
  });

export function useRecordExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExpenseInput) => api.post<Expense>('/finance/expenses', body),
    onSuccess: () => invalidateFinance(qc),
  });
}

/**
 * Revises an expense that has not been ruled on yet.
 *
 * The server refuses a reviewed row, so the caller only offers this while the
 * expense is pending; `receiptId: null` detaches the receipt, absent leaves it.
 */
export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<ExpenseInput> & { id: string }) =>
      api.patch<Expense>(`/finance/expenses/${id}`, body),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useReviewExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      status: 'approved' | 'rejected';
      note?: string;
    }) => api.post<Expense>(`/finance/expenses/${id}/review`, body),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/finance/expenses/${id}`),
    onSuccess: () => invalidateFinance(qc),
  });
}

/* ---------------------------- my own claims ----------------------------- */

/**
 * The member's side of the books, which is not the books: their own claims and
 * the receipts they filed. A separate key from `['expenses']` because the two
 * lists answer different questions and only one of them is permitted to most
 * people — sharing a cache key would mean a member's list being invalidated by
 * an owner's review, or worse, filled by it.
 */
export const useMyExpenses = (enabled = true) =>
  useQuery({
    queryKey: ['my-expenses'],
    queryFn: () => api.get<List<Expense>>('/finance/mine/expenses'),
    staleTime: 30_000,
    enabled,
  });

export function useSubmitExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExpenseInput) => api.post<Expense>('/finance/mine/expenses', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-expenses'] }),
  });
}

export function useUpdateMyExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<ExpenseInput> & { id: string }) =>
      api.patch<Expense>(`/finance/mine/expenses/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-expenses'] }),
  });
}

/** Withdrawn rather than deleted: only a claim nobody has ruled on can go. */
export function useWithdrawExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/finance/mine/expenses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-expenses'] }),
  });
}

export function useUploadMyReceipt() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<FinanceFile>('/finance/mine/files', form);
    },
  });
}

export function useReadMyReceipt() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<FinanceReading>(`/finance/mine/files/${id}/read`, { target: 'expense' }),
  });
}

export function useMyReceiptUrl() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ url: string; expiresAt: string }>(`/finance/mine/files/${id}/access`),
  });
}

interface StatementInput {
  title: string;
  kind: StatementKind;
  periodStart: string;
  periodEnd: string;
  scope: FinanceScope;
  departmentId?: string | null;
  projectId?: string | null;
  note?: string;
  fileId: string;
}

/**
 * Uploads one document for the books and hands back the row to attach it to.
 *
 * Separate from the record it belongs to because both a receipt and a
 * statement need it, and because a browser cannot send a file and a JSON body
 * in one request without turning every field into a string.
 */
export function useUploadFinanceFile() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<FinanceFile>('/finance/files', form);
    },
  });
}

/**
 * Reads a just-uploaded document and suggests what the form should say.
 *
 * Deliberately not chained onto the upload: the document is filed whether or
 * not it can be read, and a person who has already typed the amount should not
 * be made to wait for a suggestion they do not need.
 */
export function useReadFinanceFile() {
  return useMutation({
    mutationFn: ({ id, target }: { id: string; target: FinanceReadTarget }) =>
      api.post<FinanceReading>(`/finance/files/${id}/read`, { target }),
  });
}

/** Trades the finance permission for a short-lived link to a document. */
export function useFinanceFileUrl() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ url: string; expiresAt: string }>(`/finance/files/${id}/access`),
  });
}

export interface StatementFilters {
  kind?: StatementKind;
  departmentId?: string;
  projectId?: string;
}

export const useStatements = (filters: StatementFilters = {}, enabled = true) =>
  useQuery({
    queryKey: ['statements', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
      const qs = params.toString();
      return api.get<List<FinancialStatement>>(`/finance/statements${qs ? `?${qs}` : ''}`);
    },
    staleTime: 60_000,
    enabled,
  });

export function useFileStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StatementInput) =>
      api.post<FinancialStatement>('/finance/statements', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['statements'] }),
  });
}

export function useDeleteStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/finance/statements/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['statements'] }),
  });
}

export const useFinanceSummary = (from: string, to: string, enabled = true) =>
  useQuery({
    queryKey: ['finance-summary', from, to],
    queryFn: () => api.get<FinanceSummary>(`/finance/summary?from=${from}&to=${to}`),
    staleTime: 60_000,
    enabled,
  });

/* ------------------------------ bookings -------------------------------- */

/**
 * Desks and rooms held on one floor.
 *
 * Everybody's, not just the reader's: the point of the panel and of the
 * badges on the floor is to see who has what before you pick.
 */
export const useSpaceBookings = (spaceId?: string, range?: { from: string; to: string }) =>
  useQuery({
    queryKey: ['bookings', 'space', spaceId, range],
    queryFn: () => {
      const params = new URLSearchParams({ spaceId: spaceId! });
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
      return api.get<List<Booking>>(`/bookings?${params}`);
    },
    enabled: Boolean(spaceId),
  });

/** Your own bookings, for the calendar. */
export const useMyBookings = (range?: { from: string; to: string }) =>
  useQuery({
    queryKey: ['bookings', 'mine', range],
    queryFn: () => {
      const params = new URLSearchParams();
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
      return api.get<List<Booking>>(`/bookings/mine${params.size ? `?${params}` : ''}`);
    },
  });

/** Free/busy for every bookable thing on a floor, over one window. */
export const useSpaceAvailability = (spaceId?: string, range?: { from: string; to: string }) =>
  useQuery({
    queryKey: ['availability', spaceId, range],
    queryFn: () => {
      const params = new URLSearchParams();
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
      return api.get<List<ResourceAvailability>>(
        `/spaces/${spaceId}/availability${params.size ? `?${params}` : ''}`,
      );
    },
    enabled: Boolean(spaceId),
  });

export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      spaceId: string;
      zoneId?: string;
      furnitureId?: string;
      title?: string;
      start: string;
      end: string;
      meetingId?: string;
      repeatThisWeek?: boolean;
    }) => api.post<{ booking: Booking; repeats: Booking[] }>('/bookings', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['availability'] });
    },
  });
}

export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/bookings/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['availability'] });
    },
  });
}
